import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';
import mysteryBoxBg from '../assets/images/mystery-box-bg.png';
import mysteryBoxTitle from '../assets/images/mystery-box-title.png';
import mysteryBoxCardBg from '../assets/images/mystery-box-card-bg.png';
import chestGreen from '../assets/images/chest-green.png';
import chestRed from '../assets/images/chest-red.png';
import chestPurple from '../assets/images/chest-purple.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WebSocket URL (converte http:// para ws:// ou https:// para wss://)
const WS_URL = BACKEND_URL?.replace('http://', 'ws://').replace('https://', 'wss://');

// Sub-abas do cliente (Jackpot como primeira) - Grid responsivo com abas de tamanho igual
const ClientTabs = ({ activeTab, setActiveTab, jackpotActive }) => {
  // Define o grid baseado em quantas abas existem (3 ou 4 se jackpot ativo)
  const gridCols = jackpotActive ? 'grid-cols-4' : 'grid-cols-3';
  
  return (
    <div className={`grid ${gridCols} gap-1.5 bg-purple-900/50 p-1.5 rounded-xl backdrop-blur-sm border border-purple-500/30`}>
      {jackpotActive && (
        <button
          onClick={() => setActiveTab('jackpot')}
          className={`py-2 px-1 text-[11px] sm:text-sm font-bold rounded-lg transition-all text-center truncate ${
            activeTab === 'jackpot'
              ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-purple-900 shadow-lg shadow-yellow-500/30 animate-pulse'
              : 'text-purple-200 hover:text-white hover:bg-purple-500/20'
          }`}
        >
          🎰 Jackpot
        </button>
      )}
      <button
        onClick={() => setActiveTab('event')}
        className={`py-2 px-1 text-[11px] sm:text-sm font-bold rounded-lg transition-all text-center truncate ${
          activeTab === 'event'
            ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-purple-900 shadow-lg shadow-yellow-500/30'
            : 'text-purple-200 hover:text-white hover:bg-purple-500/20'
        }`}
      >
        Evento
      </button>
      <button
        onClick={() => setActiveTab('history')}
        className={`py-2 px-1 text-[11px] sm:text-sm font-bold rounded-lg transition-all text-center truncate ${
          activeTab === 'history'
            ? 'bg-gradient-to-r from-purple-400 to-fuchsia-500 text-white shadow-lg shadow-purple-500/30'
            : 'text-purple-200 hover:text-white hover:bg-purple-500/20'
        }`}
      >
        Histórico
      </button>
      <button
        onClick={() => setActiveTab('rules')}
        className={`py-2 px-1 text-[11px] sm:text-sm font-bold rounded-lg transition-all text-center truncate ${
          activeTab === 'rules'
            ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg shadow-cyan-500/30'
            : 'text-purple-200 hover:text-white hover:bg-purple-500/20'
        }`}
      >
        Regras
      </button>
    </div>
  );
};

// Componente de Partículas Mágicas (otimizado)
const MagicParticles = ({ active, intensity = 'normal' }) => {
  const particles = useMemo(() => {
    const count = intensity === 'high' ? 40 : 20;
    return [...Array(count)].map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
      size: 4 + Math.random() * 6,
      color: ['#FFD700', '#FFA500', '#FFEC8B', '#FFE4B5'][i % 4]
    }));
  }, [intensity]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full animate-particle-float"
          style={{
            left: `${p.left}%`,
            bottom: '20%',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`
          }}
        />
      ))}
    </div>
  );
};

// Raios de Luz Girantes
const LightBeams = ({ active }) => {
  if (!active) return null;
  
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
      <div className="absolute w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] animate-spin-slow">
        {[...Array(16)].map((_, i) => (
          <div
            key={i}
            className="absolute left-1/2 top-1/2 w-1 sm:w-1.5 origin-bottom"
            style={{
              height: '150px',
              transform: `rotate(${i * 22.5}deg) translateX(-50%)`,
              background: 'linear-gradient(to top, transparent, rgba(255, 215, 0, 0.6), transparent)',
              filter: 'blur(2px)'
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Baú 3D Aprimorado
// Mapa de raridade -> cor (glow/aura)
const RARITY_GLOW = {
  // Combinações: ring color (hex), glow color (rgba), confetti color, label color
  100: { ring: '#a855f7', glow: 'rgba(168,85,247,0.85)', label: 'text-purple-300' }, // Lendário
  50:  { ring: '#ec4899', glow: 'rgba(236,72,153,0.85)', label: 'text-pink-300' },   // Épico
  25:  { ring: '#3b82f6', glow: 'rgba(59,130,246,0.85)', label: 'text-blue-300' },   // Raro
  20:  { ring: '#22c55e', glow: 'rgba(34,197,94,0.85)',  label: 'text-emerald-300' },// Incomum
  10:  { ring: '#94a3b8', glow: 'rgba(148,163,184,0.85)', label: 'text-slate-300' }, // Comum
  5:   { ring: '#a8a29e', glow: 'rgba(168,162,158,0.85)', label: 'text-stone-300' }, // Básico
};

const getRarityStyle = (points) => RARITY_GLOW[points] || RARITY_GLOW[5];

const TreasureChest = ({ isShaking, isOpen, showPrize, prizeAmount, prizeName, prizeImageUrl }) => {
  const rarity = getRarityStyle(prizeAmount || 0);
  const won = prizeAmount > 0;

  return (
    <div className="relative flex flex-col items-center" style={{ minHeight: '280px' }}>
      {/* PALCO — container fixo com tudo centralizado e overflow visível */}
      <div className="relative w-72 h-72 sm:w-80 sm:h-80 flex items-center justify-center" style={{ perspective: '1200px' }}>

        {/* CAMADA 1: Light beams saindo do baú (apenas quando abrindo) */}
        {isOpen && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
            <div
              className="absolute w-72 h-72 sm:w-96 sm:h-96 animate-light-beams"
              style={{
                background: won
                  ? `conic-gradient(from 0deg, transparent 0deg, ${rarity.glow} 5deg, transparent 30deg, ${rarity.glow} 60deg, transparent 90deg, ${rarity.glow} 120deg, transparent 180deg, ${rarity.glow} 220deg, transparent 270deg, ${rarity.glow} 300deg, transparent 360deg)`
                  : 'transparent',
                borderRadius: '50%',
                filter: 'blur(3px)',
                opacity: 0.55,
              }}
            />
            <div
              className="absolute w-56 h-56 sm:w-72 sm:h-72 rounded-full animate-glow-pulse-strong"
              style={{
                background: `radial-gradient(circle, ${rarity.glow} 0%, transparent 70%)`,
                opacity: won ? 1 : 0.3,
              }}
            />
          </div>
        )}

        {/* CAMADA 2: O BAÚ (centrado no palco) */}
        <div
          className={`
            relative w-36 h-36 sm:w-48 sm:h-48 z-10
            transition-all duration-500
            ${isShaking ? 'animate-chest-shake' : ''}
            ${!isShaking && !isOpen ? 'hover:scale-105 cursor-pointer' : ''}
            ${showPrize && won ? 'opacity-70 scale-95' : 'opacity-100'}
          `}
          style={{ perspective: '1000px' }}
        >
          {isShaking && (
            <div className="absolute inset-0 rounded-lg animate-glow-pulse"
                 style={{ boxShadow: '0 0 50px rgba(255, 215, 0, 0.9)' }} />
          )}

          {/* Base do Baú */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-28 sm:w-40 h-16 sm:h-24">
            <div className="relative w-full h-full bg-gradient-to-b from-amber-500 via-amber-600 to-amber-800 rounded-lg border-4 border-amber-900 shadow-2xl overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-1 bg-amber-950 my-2" style={{ marginLeft: i % 2 ? '10%' : '5%', width: i % 2 ? '80%' : '90%' }} />
                ))}
              </div>
              <div className="absolute inset-x-2 top-1.5 sm:top-2 h-0.5 sm:h-1 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 rounded" />
              <div className="absolute inset-x-2 bottom-1.5 sm:bottom-2 h-0.5 sm:h-1 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 rounded" />
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-6 sm:w-10 h-6 sm:h-10">
                <div className="w-full h-full bg-gradient-to-br from-yellow-300 via-yellow-500 to-yellow-700 rounded-full border-2 border-yellow-800 shadow-lg flex items-center justify-center">
                  <div className="w-2.5 sm:w-4 h-1.5 sm:h-2 bg-amber-900 rounded-sm" />
                </div>
              </div>
              <div className="absolute top-0 left-0 w-3 sm:w-4 h-3 sm:h-4 border-l-2 border-t-2 border-yellow-400 rounded-tl" />
              <div className="absolute top-0 right-0 w-3 sm:w-4 h-3 sm:h-4 border-r-2 border-t-2 border-yellow-400 rounded-tr" />
              <div className="absolute bottom-0 left-0 w-3 sm:w-4 h-3 sm:h-4 border-l-2 border-b-2 border-yellow-400 rounded-bl" />
              <div className="absolute bottom-0 right-0 w-3 sm:w-4 h-3 sm:h-4 border-r-2 border-b-2 border-yellow-400 rounded-br" />
            </div>
          </div>

          {/* Tampa do Baú */}
          <div
            className={`
              absolute bottom-14 sm:bottom-20 left-1/2 -translate-x-1/2 
              w-32 sm:w-44 h-10 sm:h-16 
              origin-bottom transition-transform duration-700
              ${isOpen ? 'animate-lid-open' : ''}
            `}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="relative w-full h-full bg-gradient-to-b from-amber-400 via-amber-500 to-amber-700 rounded-t-[2rem] sm:rounded-t-[3rem] border-4 border-amber-900 shadow-xl overflow-hidden">
              <div className="absolute inset-x-3 sm:inset-x-4 top-1 sm:top-2 h-5 sm:h-8 border-3 sm:border-4 border-yellow-400 rounded-t-2xl border-b-0" />
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-8 sm:w-12 h-1 sm:h-1.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded" />
            </div>
          </div>

          {/* Brilho interno quando aberto */}
          {isOpen && (
            <div className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 w-20 sm:w-28 h-12 sm:h-16 z-0">
              <div className="w-full h-full bg-gradient-to-t from-yellow-500 via-yellow-300 to-white rounded-full blur-lg opacity-90 animate-pulse-slow" />
            </div>
          )}
        </div>

        {/* CAMADA 3: PRÊMIO EMERGE DE DENTRO DO BAÚ (centro do palco, sobreposto) */}
        {showPrize && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
            {won ? (
              <div
                className="relative animate-prize-emerge"
                style={{ '--rarity-ring': rarity.ring, '--rarity-glow': rarity.glow }}
              >
                {/* Halo radial atrás (cor da raridade) */}
                <div
                  className="absolute inset-0 -m-12 sm:-m-16 rounded-full animate-pulse"
                  style={{
                    background: `radial-gradient(circle, ${rarity.glow} 0%, transparent 65%)`,
                    filter: 'blur(8px)',
                  }}
                />

                {/* Estrelas girando ao redor */}
                <div className="absolute inset-0 -m-6 sm:-m-8 animate-spin-slow">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-3 h-3 sm:w-4 sm:h-4"
                      style={{
                        left: `${50 + 55 * Math.cos((i * Math.PI) / 4)}%`,
                        top: `${50 + 55 * Math.sin((i * Math.PI) / 4)}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="w-full h-full fill-current drop-shadow-lg animate-twinkle"
                           style={{ color: rarity.ring, animationDelay: `${i * 0.12}s` }}>
                        <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z" />
                      </svg>
                    </div>
                  ))}
                </div>

                {/* Imagem do prêmio (destaque total, sem fundo concorrente) */}
                <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center">
                  {prizeImageUrl ? (
                    <img
                      src={prizeImageUrl}
                      alt={prizeName || 'Prêmio'}
                      className="w-full h-full object-contain"
                      style={{
                        filter: `drop-shadow(0 0 18px ${rarity.glow}) drop-shadow(0 6px 12px rgba(0,0,0,0.5))`,
                      }}
                    />
                  ) : (
                    <span className="text-7xl sm:text-8xl drop-shadow-2xl"
                          style={{ filter: `drop-shadow(0 0 18px ${rarity.glow})` }}>
                      🪙
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative animate-prize-emerge text-center">
                <div className="text-5xl sm:text-7xl mb-2">💨</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RESULTADO ABAIXO — painel fixo, centralizado, claro */}
      {showPrize && (
        <div className="mt-2 sm:mt-4 text-center animate-result-fade-in">
          {won ? (
            <>
              <div
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full shadow-2xl"
                style={{
                  background: `linear-gradient(135deg, ${rarity.ring}, ${rarity.glow})`,
                  boxShadow: `0 0 24px ${rarity.glow}`,
                }}
              >
                <span className="text-2xl sm:text-3xl font-black text-white tracking-tight drop-shadow-lg">
                  +{prizeAmount.toLocaleString()}
                </span>
                <span className="text-xs sm:text-sm font-bold text-white/90 uppercase tracking-widest">pts</span>
              </div>
              {prizeName && (
                <p className={`mt-2 text-sm sm:text-base font-bold drop-shadow-lg ${rarity.label}`}>
                  {prizeName}
                </p>
              )}
            </>
          ) : (
            <p className="text-base sm:text-lg text-gray-400 font-medium">Baú vazio — Tente novamente!</p>
          )}
        </div>
      )}
    </div>
  );
};

// Estilos CSS das animações
const AnimationStyles = () => (
  <style>{`
    @keyframes particle-float {
      0% { transform: translateY(0) scale(1); opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { transform: translateY(-200px) scale(0.5); opacity: 0; }
    }
    
    @keyframes chest-shake {
      0%, 100% { transform: translateX(0) rotate(0deg); }
      10% { transform: translateX(-4px) rotate(-1.5deg); }
      20% { transform: translateX(4px) rotate(1.5deg); }
      30% { transform: translateX(-4px) rotate(-1.5deg); }
      40% { transform: translateX(4px) rotate(1.5deg); }
      50% { transform: translateX(-3px) rotate(-1deg); }
      60% { transform: translateX(3px) rotate(1deg); }
      70% { transform: translateX(-2px) rotate(-0.5deg); }
      80% { transform: translateX(2px) rotate(0.5deg); }
      90% { transform: translateX(-1px) rotate(0deg); }
    }
    
    @keyframes lid-open {
      0% { transform: translateX(-50%) rotateX(0deg); }
      40% { transform: translateX(-50%) rotateX(-100deg); }
      60% { transform: translateX(-50%) rotateX(-85deg); }
      80% { transform: translateX(-50%) rotateX(-95deg); }
      100% { transform: translateX(-50%) rotateX(-90deg); }
    }
    
    @keyframes glow-pulse {
      0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 0 40px rgba(255, 165, 0, 0.2); }
      50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.8), 0 0 80px rgba(255, 165, 0, 0.5), 0 0 120px rgba(255, 140, 0, 0.3); }
    }
    
    @keyframes prize-float {
      0% { transform: translateX(-50%) translateY(30px) scale(0); opacity: 0; }
      50% { transform: translateX(-50%) translateY(-10px) scale(1.1); opacity: 1; }
      100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
    }
    
    @keyframes coin-shine {
      0%, 100% { filter: brightness(1); }
      50% { filter: brightness(1.3); }
    }
    
    @keyframes twinkle {
      0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      50% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.8); }
    }
    
    @keyframes bounce-slow {
      0%, 100% { transform: translateX(-50%) translateY(0); }
      50% { transform: translateX(-50%) translateY(-5px); }
    }
    
    @keyframes pulse-slow {
      0%, 100% { opacity: 0.7; transform: translateX(-50%) scale(1); }
      50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
    }
    
    @keyframes spin-slow {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* 🎬 Animação cinematográfica do prêmio emergindo de dentro do baú */
    @keyframes prize-emerge {
      0%   { transform: translateY(30px) scale(0); opacity: 0; }
      40%  { transform: translateY(-10px) scale(1.15); opacity: 1; }
      60%  { transform: translateY(-25px) scale(1.05); opacity: 1; }
      100% { transform: translateY(-30px) scale(1); opacity: 1; }
    }

    /* ✨ Light beams girando em torno do baú aberto */
    @keyframes light-beams {
      0%   { transform: rotate(0deg) scale(0.8); opacity: 0; }
      30%  { opacity: 0.6; }
      100% { transform: rotate(360deg) scale(1.1); opacity: 0.5; }
    }

    /* 💫 Glow forte pulsante (radial) */
    @keyframes glow-pulse-strong {
      0%, 100% { transform: scale(0.9); opacity: 0.45; }
      50%      { transform: scale(1.1); opacity: 0.9; }
    }

    /* 🎈 Fade-in do painel de resultado embaixo */
    @keyframes result-fade-in {
      0%   { transform: translateY(15px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    
    .animate-particle-float { animation: particle-float 3s ease-out forwards; }
    .animate-chest-shake { animation: chest-shake 0.6s ease-in-out; }
    .animate-lid-open { animation: lid-open 0.8s ease-out forwards; }
    .animate-glow-pulse { animation: glow-pulse 0.8s ease-in-out infinite; }
    .animate-prize-float { animation: prize-float 0.6s ease-out forwards; }
    .animate-coin-shine { animation: coin-shine 2s ease-in-out infinite; }
    .animate-twinkle { animation: twinkle 1s ease-in-out infinite; }
    .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
    .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
    .animate-spin-slow { animation: spin-slow 10s linear infinite; }
    .animate-prize-emerge { animation: prize-emerge 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
    .animate-light-beams { animation: light-beams 8s linear infinite; }
    .animate-glow-pulse-strong { animation: glow-pulse-strong 2s ease-in-out infinite; }
    .animate-result-fade-in { animation: result-fade-in 0.6s ease-out 0.4s both; }
  `}</style>
);

// 🍀 Velocímetro de Sorte Acumulada
const LuckSpeedometer = ({ luck, level }) => {
  // Cores INVERTIDAS: baixa = ruim (vermelho), alta = bom (verde)
  const levelConfig = {
    baixa: { color: '#ef4444', label: 'BAIXA', glow: 'rgba(239, 68, 68, 0.6)' },
    media: { color: '#f59e0b', label: 'MÉDIA', glow: 'rgba(245, 158, 11, 0.6)' },
    alta: { color: '#10b981', label: 'ALTA', glow: 'rgba(16, 185, 129, 0.6)' }
  };
  
  const config = levelConfig[level] || levelConfig.baixa;
  
  // Calcular posição do ponteiro (0% = esquerda/vermelho, 50% = direita/verde)
  const percentage = Math.min(Math.max(luck, 0), 50) / 50;
  const angleRad = Math.PI * (1 - percentage);
  const needleLength = 28;
  const centerX = 50;
  const centerY = 45;
  const needleX = centerX + needleLength * Math.cos(angleRad);
  const needleY = centerY - needleLength * Math.sin(angleRad);
  
  return (
    <div className="flex flex-col items-center mb-3">
      {/* Card container */}
      <div className="bg-white rounded-2xl px-5 py-4 shadow-xl border border-gray-100" style={{ minWidth: '140px' }}>
        {/* Label */}
        <div className="text-center mb-3">
          <span className="text-xs font-semibold text-gray-500 tracking-wide uppercase">🍀 Sua Sorte</span>
        </div>
        
        {/* Velocímetro SVG */}
        <div className="flex justify-center">
          <svg width="120" height="75" viewBox="0 0 100 65">
            {/* Gradiente do arco: Vermelho → Amarelo → Verde */}
            <defs>
              <linearGradient id="speedGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="35%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
              <filter id="needleShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor={config.color} floodOpacity="0.5"/>
              </filter>
            </defs>
            
            {/* Arco de fundo (cinza claro) */}
            <path
              d="M 10 45 A 40 40 0 0 1 90 45"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
              strokeLinecap="round"
            />
            
            {/* Arco colorido com gradiente */}
            <path
              d="M 10 45 A 40 40 0 0 1 90 45"
              fill="none"
              stroke="url(#speedGradient)"
              strokeWidth="8"
              strokeLinecap="round"
            />
            
            {/* Linhas de divisão sutis */}
            <line x1="10" y1="45" x2="10" y2="38" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
            <line x1="50" y1="5" x2="50" y2="12" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
            <line x1="90" y1="45" x2="90" y2="38" stroke="#10b981" strokeWidth="2" strokeLinecap="round" />
            
            {/* Ponteiro */}
            <line
              x1={centerX}
              y1={centerY}
              x2={needleX}
              y2={needleY}
              stroke={config.color}
              strokeWidth="3"
              strokeLinecap="round"
              filter="url(#needleShadow)"
            />
            
            {/* Base do ponteiro */}
            <circle
              cx={centerX}
              cy={centerY}
              r="6"
              fill={config.color}
              filter="url(#needleShadow)"
            />
            <circle
              cx={centerX}
              cy={centerY}
              r="3"
              fill="white"
            />
          </svg>
        </div>
        
        {/* Badge do nível */}
        <div className="flex justify-center mt-2">
          <div 
            className="px-4 py-1.5 rounded-full text-xs font-bold tracking-wider transition-all duration-300"
            style={{ 
              backgroundColor: config.color,
              color: 'white',
              boxShadow: `0 4px 12px ${config.glow}`,
            }}
          >
            {config.label}
          </div>
        </div>
      </div>
    </div>
  );
};

// Partículas do fundo — geradas UMA vez (posições estáveis entre renders)
const BG_PARTICLES = Array.from({ length: 15 }, () => ({
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  delay: `${Math.random() * 3}s`,
}));

// Medalha/posição do ranking (estável — fora do render)
const RankBadge = ({ rank }) => {
  if (rank === 1) return <span className="text-lg">🥇</span>;
  if (rank === 2) return <span className="text-lg">🥈</span>;
  if (rank === 3) return <span className="text-lg">🥉</span>;
  return <span className="text-purple-300 font-bold text-sm">{rank}º</span>;
};

// Linha do ranking Mega Sortudos (estável — fora do render para o React
// NÃO desmontar/remontar avatares a cada tick do cronômetro)
const RankingItem = ({ winner, isCurrentUser }) => (
  <div className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
    isCurrentUser
      ? 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border border-yellow-400/50'
      : winner.rank <= 3
        ? 'bg-purple-800/30'
        : 'bg-purple-900/20'
  }`}>
    <div className="w-8 text-center">
      <RankBadge rank={winner.rank} />
    </div>
    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-purple-500/50 bg-purple-700 flex-shrink-0">
      {winner.kako_avatar ? (
        <img src={winner.kako_avatar} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-sm">👤</div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className={`text-sm truncate ${isCurrentUser ? 'text-yellow-300 font-bold' : 'text-white'}`}>
          {winner.kako_nickname}
        </p>
        {isCurrentUser && (
          <span className="px-1.5 py-0.5 bg-yellow-500 text-purple-900 text-[10px] font-bold rounded">
            VOCÊ
          </span>
        )}
      </div>
      <p className="text-purple-400 text-xs">{winner.total_boxes} baú{winner.total_boxes > 1 ? 's' : ''}</p>
    </div>
    <div className="text-right">
      <p className={`font-bold text-sm ${winner.rank <= 3 ? 'text-yellow-400' : 'text-purple-200'}`}>
        {winner.total_points.toLocaleString()} ⭐
      </p>
      {winner.prize > 0 && (
        <p className="text-green-400 text-xs font-bold">+{winner.prize.toLocaleString()} 🪙</p>
      )}
    </div>
  </div>
);

const MysteryBoxPage = ({ kakoId, onBoxOpened }) => {
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showPrize, setShowPrize] = useState(false);
  const [prizeAmount, setPrizeAmount] = useState(null);
  const [prizeName, setPrizeName] = useState(null);
  const [prizeImageUrl, setPrizeImageUrl] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [activeTab, setActiveTab] = useState('event');
  const [rules, setRules] = useState('');
  const [topWinnersCurrent, setTopWinnersCurrent] = useState([]);
  const [topWinnersPrevious, setTopWinnersPrevious] = useState([]);
  const [winnersPeriod, setWinnersPeriod] = useState('current');
  const [weeklyPrizes, setWeeklyPrizes] = useState({ 1: 0, 2: 0, 3: 0 });
  const [myWeeklyPrizes, setMyWeeklyPrizes] = useState([]);
  const [weekInfo, setWeekInfo] = useState({ current: '', previous: '' });
  const [claimingPrizeId, setClaimingPrizeId] = useState(null); // ID do prêmio sendo resgatado
  const [showLoginPrompt, setShowLoginPrompt] = useState(false); // exige conta p/ coletar
  const [userRankingData, setUserRankingData] = useState({ current: null, previous: null }); // Posição do usuário no ranking
  const [showFullRanking, setShowFullRanking] = useState(false); // Controle para mostrar ranking completo
  
  // Jackpot States
  const [jackpot, setJackpot] = useState(null);
  const [jackpotClaiming, setJackpotClaiming] = useState(false);
  const [jackpotCooldown, setJackpotCooldown] = useState(0);
  const [jackpotResult, setJackpotResult] = useState(null); // Resultado da tentativa
  const [onlineUsers, setOnlineUsers] = useState([]); // Usuários online no Jackpot
  const [showOnlineUsers, setShowOnlineUsers] = useState(false); // Mostrar/esconder lista
  const [showResultModal, setShowResultModal] = useState(false); // Modal de resultado
  const [wsConnected, setWsConnected] = useState(false); // Status da conexão WebSocket
  
  // Chat ao Vivo States
  const [chatMessages, setChatMessages] = useState([]); // Mensagens do chat
  const [chatInput, setChatInput] = useState(''); // Input do usuário
  const [chatError, setChatError] = useState(null); // Erro temporário
  const [showChat, setShowChat] = useState(true); // Mostrar/esconder chat
  const [chatSending, setChatSending] = useState(false); // Proteção contra duplo clique
  const chatContainerRef = useRef(null); // Ref para scroll automático
  const lastSentMessageRef = useRef(''); // Anti-duplicação: última mensagem enviada
  const [sessionReplaced, setSessionReplaced] = useState(false); // 🔐 Sessão substituída por outro dispositivo
  
  // 🌐 Tradutor do Chat
  const [chatLang, setChatLang] = useState(() => {
    const saved = localStorage.getItem('jackpot_chat_lang');
    return saved || 'pt'; // 'pt', 'es', 'en'
  });
  const [translatorEnabled, setTranslatorEnabled] = useState(() => {
    const saved = localStorage.getItem('jackpot_translator_enabled');
    return saved !== null ? saved === 'true' : true; // Ativado por padrão
  });
  const [translatedMessages, setTranslatedMessages] = useState({}); // Cache de traduções
  const [isTranslating, setIsTranslating] = useState(false);
  const translationCacheRef = useRef({}); // Cache persistente durante a sessão
  
  // Salvar preferência do tradutor no localStorage
  useEffect(() => {
    localStorage.setItem('jackpot_translator_enabled', translatorEnabled.toString());
  }, [translatorEnabled]);
  
  // Função para traduzir texto
  // Usa detecção automática de idioma de origem
  // Só traduz se o idioma de origem for diferente do idioma alvo
  const translateText = useCallback(async (text, targetLang) => {
    if (!text || text.trim().length === 0) return null;
    
    // Verificar cache
    const cacheKey = `${text}_${targetLang}`;
    if (translationCacheRef.current[cacheKey] !== undefined) {
      return translationCacheRef.current[cacheKey]; // Pode ser null se não precisou traduzir
    }
    
    try {
      // Usar API MyMemory com detecção automática de idioma de origem
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=autodetect|${targetLang}`
      );
      const data = await response.json();
      
      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        const translated = data.responseData.translatedText;
        
        // Verificar se realmente traduziu (texto diferente)
        // Se o texto traduzido é igual ao original, significa que já estava no idioma alvo
        const originalClean = text.trim().toLowerCase();
        const translatedClean = translated.trim().toLowerCase();
        
        if (translatedClean === originalClean) {
          // Não precisou traduzir - já estava no idioma certo
          translationCacheRef.current[cacheKey] = null;
          return null;
        }
        
        // Traduziu com sucesso
        translationCacheRef.current[cacheKey] = translated;
        return translated;
      }
      
      // Falhou - cacheia como null para não tentar novamente
      translationCacheRef.current[cacheKey] = null;
      return null;
    } catch (error) {
      console.log('Erro na tradução:', error);
      translationCacheRef.current[cacheKey] = null;
      return null;
    }
  }, []);
  
  // Traduzir mensagens quando o idioma mudar ou novas mensagens chegarem
  // REGRA: Traduz mensagens de OUTROS usuários para o idioma selecionado
  // SÓ traduz se o tradutor estiver ATIVADO
  useEffect(() => {
    const translateMessages = async () => {
      // Se tradutor desativado, limpa traduções
      if (!translatorEnabled || chatMessages.length === 0) {
        setTranslatedMessages({});
        return;
      }
      
      // Filtrar apenas mensagens de OUTROS usuários para traduzir
      const messagesToTranslate = chatMessages.filter(msg => 
        msg.kako_id !== kakoId && msg.type !== 'system'
      );
      
      if (messagesToTranslate.length === 0) {
        // Traduzir apenas mensagens de sistema se houver
        const systemMessages = chatMessages.filter(msg => msg.type === 'system');
        if (systemMessages.length > 0) {
          setIsTranslating(true);
          const newTranslations = {};
          for (const msg of systemMessages) {
            const msgKey = `${msg.id || msg.timestamp}_${chatLang}`;
            const cacheKey = `${msg.message}_${chatLang}`;
            if (translationCacheRef.current[cacheKey]) {
              newTranslations[msgKey] = translationCacheRef.current[cacheKey];
            } else {
              const translated = await translateText(msg.message, chatLang);
              newTranslations[msgKey] = translated;
            }
          }
          setTranslatedMessages(newTranslations);
          setIsTranslating(false);
        }
        return;
      }
      
      setIsTranslating(true);
      const newTranslations = { ...translatedMessages };
      
      for (const msg of messagesToTranslate) {
        const msgKey = `${msg.id || msg.timestamp}_${chatLang}`;
        const cacheKey = `${msg.message}_${chatLang}`;
        
        // Verificar se já tem no cache
        if (translationCacheRef.current[cacheKey]) {
          newTranslations[msgKey] = translationCacheRef.current[cacheKey];
        } else if (!newTranslations[msgKey]) {
          // Traduzir de qualquer idioma para o idioma selecionado
          const translated = await translateText(msg.message, chatLang);
          newTranslations[msgKey] = translated;
        }
      }
      
      // Também traduzir mensagens de sistema
      const systemMessages = chatMessages.filter(msg => msg.type === 'system');
      for (const msg of systemMessages) {
        const msgKey = `${msg.id || msg.timestamp}_${chatLang}`;
        const cacheKey = `${msg.message}_${chatLang}`;
        if (translationCacheRef.current[cacheKey]) {
          newTranslations[msgKey] = translationCacheRef.current[cacheKey];
        } else if (!newTranslations[msgKey]) {
          const translated = await translateText(msg.message, chatLang);
          newTranslations[msgKey] = translated;
        }
      }
      
      setTranslatedMessages(newTranslations);
      setIsTranslating(false);
    };
    
    translateMessages();
  }, [chatLang, chatMessages, translateText, kakoId, translatorEnabled]);
  
  // Salvar preferência de idioma do chat
  useEffect(() => {
    localStorage.setItem('jackpot_chat_lang', chatLang);
  }, [chatLang]);
  
  // Função para obter mensagem traduzida ou original
  // REGRA: Só traduz se tradutor ATIVADO e mensagens de OUTROS usuários
  const getTranslatedMessage = useCallback((msg) => {
    // Se tradutor desativado, sempre mostra original
    if (!translatorEnabled) return msg.message;
    
    // Se é mensagem do próprio usuário, NÃO traduz
    if (msg.kako_id === kakoId && msg.type !== 'system') {
      return msg.message;
    }
    
    // Mensagem de outro usuário ou sistema: usa tradução se disponível e diferente
    const msgKey = `${msg.id || msg.timestamp}_${chatLang}`;
    const translated = translatedMessages[msgKey];
    
    // Se translated é null ou undefined, mostra original
    // null significa que já estava no idioma certo (não precisou traduzir)
    return translated || msg.message;
  }, [chatLang, translatedMessages, kakoId, translatorEnabled]);
  
  // Verificar se mensagem foi traduzida
  // Só mostra ícone 🌐 quando realmente houve tradução
  const isMessageTranslated = useCallback((msg) => {
    // Se tradutor desativado, nunca mostra ícone
    if (!translatorEnabled) return false;
    
    // Não mostra ícone nas próprias mensagens
    if (msg.kako_id === kakoId && msg.type !== 'system') return false;
    
    const msgKey = `${msg.id || msg.timestamp}_${chatLang}`;
    const translated = translatedMessages[msgKey];
    
    // Só mostra ícone se translated existe e é diferente do original
    // null ou undefined = não traduziu (já estava no idioma certo)
    return translated && translated.trim().toLowerCase() !== msg.message.trim().toLowerCase();
  }, [chatLang, translatedMessages, kakoId, translatorEnabled]);
  
  // 🍀 Estado de Sorte Acumulada
  const [userLuck, setUserLuck] = useState({ luck: 0, level: 'baixa' });
  
  // 📜 Modal de Regras do Jackpot
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesLang, setRulesLang] = useState('pt'); // 'pt' ou 'es'
  
  // 🔔 Notificação Sonora
  const [soundEnabled, setSoundEnabled] = useState(() => {
    // Recuperar preferência do localStorage
    const saved = localStorage.getItem('jackpot_sound_enabled');
    return saved !== null ? saved === 'true' : true; // Ativado por padrão
  });
  const audioContextRef = useRef(null);
  
  // Função para tocar som de notificação
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      // Criar AudioContext se não existir
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Criar som de notificação (melodia de "baú liberado")
      const playTone = (freq, startTime, duration) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = ctx.currentTime;
      // Sequência de notas: celebração!
      playTone(523.25, now, 0.15);        // C5
      playTone(659.25, now + 0.15, 0.15); // E5
      playTone(783.99, now + 0.30, 0.15); // G5
      playTone(1046.50, now + 0.45, 0.3); // C6 (mais longo)
      
    } catch (error) {
      console.log('Erro ao tocar som:', error);
    }
  }, [soundEnabled]);
  
  // Salvar preferência de som no localStorage
  useEffect(() => {
    localStorage.setItem('jackpot_sound_enabled', soundEnabled.toString());
  }, [soundEnabled]);
  
  // Ref para acessar o valor atual de jackpotResult no WebSocket handler
  const jackpotResultRef = useRef(null);
  useEffect(() => {
    jackpotResultRef.current = jackpotResult;
  }, [jackpotResult]);

  // Ref estável para o callback do pai (evita recriar loadData a cada render da Home)
  const onBoxOpenedRef = useRef(onBoxOpened);
  useEffect(() => {
    onBoxOpenedRef.current = onBoxOpened;
  }, [onBoxOpened]);
  
  // Scroll automático quando chegam novas mensagens
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const loadData = useCallback(async () => {
    if (!kakoId) return;
    
    try {
      const [progressRes, historyRes, rulesRes, winnersCurrentRes, winnersPreviousRes, myPrizesRes, jackpotRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/mystery-box/progress/${kakoId}`),
        axios.get(`${BACKEND_URL}/api/mystery-box/history/${kakoId}`),
        axios.get(`${BACKEND_URL}/api/mystery-box/rules`),
        axios.get(`${BACKEND_URL}/api/mystery-box/top-winners?period=current&kako_id=${kakoId}&limit=20`),
        axios.get(`${BACKEND_URL}/api/mystery-box/top-winners?period=previous&kako_id=${kakoId}&limit=20`),
        axios.get(`${BACKEND_URL}/api/mystery-box/weekly-ranking/my-prizes/${kakoId}`),
        axios.get(`${BACKEND_URL}/api/mystery-box/jackpot/status?kako_id=${kakoId}`)
      ]);
      
      setProgress(progressRes.data);
      setHistory(historyRes.data?.boxes || historyRes.data || []);
      setRules(rulesRes.data?.rules || '');
      setTopWinnersCurrent(winnersCurrentRes.data?.winners || []);
      setTopWinnersPrevious(winnersPreviousRes.data?.winners || []);
      setWeeklyPrizes(winnersCurrentRes.data?.weekly_prizes || { 1: 0, 2: 0, 3: 0 });
      setMyWeeklyPrizes(myPrizesRes.data?.prizes || []);
      
      // 🔄 DETECTAR MUDANÇA DE STATUS: Se mudou para 'available', resetar estados
      setJackpot(prev => {
        const newStatus = jackpotRes.data?.status;
        const prevStatus = prev?.status;
        const newChestAvailable = prevStatus && prevStatus !== newStatus && newStatus === 'available';
        
        if (newChestAvailable) {
          console.log('🆕 [loadData] NOVO BAÚ DISPONÍVEL! Resetando estados...');
          setJackpotClaiming(false);
          setJackpotResult(null);
          isJackpotProcessingRef.current = false;
          if (claimTimeoutRef.current) {
            clearTimeout(claimTimeoutRef.current);
            claimTimeoutRef.current = null;
          }
          lastClaimAttemptRef.current = 0;
        }
        
        return jackpotRes.data;
      });
      
      // Se jackpot está ativo e em cooldown, iniciar contador
      if (jackpotRes.data?.cooldown_remaining > 0) {
        setJackpotCooldown(jackpotRes.data.cooldown_remaining);
      }
      // 🆕 Se baú está disponível, usar countdown do available
      else if (jackpotRes.data?.status === 'available' && jackpotRes.data?.available_countdown > 0) {
        setJackpotCooldown(jackpotRes.data.available_countdown);
      }
      
      // Dados do usuário no ranking
      setUserRankingData({
        current: {
          position: winnersCurrentRes.data?.user_position,
          data: winnersCurrentRes.data?.user_data,
          pointsToClimb: winnersCurrentRes.data?.points_to_climb,
          isInTop: winnersCurrentRes.data?.is_user_in_top,
          totalParticipants: winnersCurrentRes.data?.total_participants
        },
        previous: {
          position: winnersPreviousRes.data?.user_position,
          data: winnersPreviousRes.data?.user_data,
          pointsToClimb: winnersPreviousRes.data?.points_to_climb,
          isInTop: winnersPreviousRes.data?.is_user_in_top,
          totalParticipants: winnersPreviousRes.data?.total_participants
        }
      });
      
      // Info das semanas
      setWeekInfo({
        current: `${winnersCurrentRes.data?.week_start || ''} a ${winnersCurrentRes.data?.week_end || ''}`,
        previous: `${winnersPreviousRes.data?.week_start || ''} a ${winnersPreviousRes.data?.week_end || ''}`
      });
      
      if (progressRes.data.time_remaining_seconds) {
        setTimeRemaining(progressRes.data.time_remaining_seconds);
      }
      
      // Notifica o pai sobre a quantidade de baús pendentes (via ref estável)
      if (onBoxOpenedRef.current) {
        onBoxOpenedRef.current(progressRes.data?.pending_boxes || 0);
      }
      
      // Retornar dados para uso externo
      return { progress: progressRes.data, jackpot: jackpotRes.data };
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [kakoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cronômetro regressivo
  useEffect(() => {
    if (timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Cronômetro regressivo do Jackpot Cooldown
  useEffect(() => {
    if (jackpotCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setJackpotCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // 🆕 COOLDOWN TERMINOU: Resetar estados para novo baú
          console.log('⏰ Cooldown terminou! Resetando estados para novo baú...');
          setJackpotClaiming(false);
          setJackpotResult(null);
          isJackpotProcessingRef.current = false;
          if (claimTimeoutRef.current) {
            clearTimeout(claimTimeoutRef.current);
            claimTimeoutRef.current = null;
          }
          lastClaimAttemptRef.current = 0;
          // Recarregar dados do jackpot quando cooldown terminar
          loadData();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [jackpotCooldown, loadData]);

  // 🔌 WebSocket para atualizações em tempo real do Jackpot
  const wsRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);
  const wsReconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  
  // Refs para controle do claim (declaradas aqui para usar no WebSocket handler)
  const claimTimeoutRef = useRef(null);
  const isJackpotProcessingRef = useRef(false);
  const lastClaimAttemptRef = useRef(0);
  
  useEffect(() => {
    // Só conecta ao WebSocket se jackpot estiver ativo e usuário estiver na aba jackpot
    if (!jackpot?.active || activeTab !== 'jackpot') {
      // Desconecta se sair da aba ou jackpot desativado
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }
    
    const connectWebSocket = () => {
      try {
        // Limpa timeout anterior se existir
        if (wsReconnectTimeoutRef.current) {
          clearTimeout(wsReconnectTimeoutRef.current);
          wsReconnectTimeoutRef.current = null;
        }
        
        const ws = new WebSocket(`${WS_URL}/api/mystery-box/jackpot/ws`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('🔌 Jackpot WebSocket conectado');
          setWsConnected(true);
          wsReconnectAttempts.current = 0; // Reset tentativas
          
          // Envia identificação do usuário
          if (kakoId) {
            ws.send(JSON.stringify({
              type: 'identify',
              kako_id: kakoId,
              kako_nickname: progress?.kako_nickname || '',
              kako_avatar: progress?.kako_avatar || ''
            }));
          }
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📡 Jackpot WS:', data.type);
            
            if (data.type === 'initial_status' || data.type === 'progress_update') {
              // Atualiza o estado do jackpot com os novos dados
              setJackpot(prev => {
                const currentResult = jackpotResultRef.current;
                const preserveLastWinner = prev?.last_winner && !currentResult;
                
                // 🔄 DETECTAR MUDANÇA DE STATUS: Se mudou para 'available', resetar estados
                const statusChanged = prev?.status !== data.status;
                const newChestAvailable = statusChanged && data.status === 'available';
                
                if (newChestAvailable) {
                  console.log('🆕 NOVO BAÚ DISPONÍVEL! Resetando estados...');
                  // 🔔 Tocar som de notificação
                  playNotificationSound();
                  // Resetar todos os estados de participação do baú anterior
                  setJackpotClaiming(false);
                  setJackpotResult(null);
                  isJackpotProcessingRef.current = false;
                  if (claimTimeoutRef.current) {
                    clearTimeout(claimTimeoutRef.current);
                    claimTimeoutRef.current = null;
                  }
                  lastClaimAttemptRef.current = 0;
                }
                
                return {
                  ...prev,
                  progress_percent: data.progress_percent,
                  status: data.status,
                  prize_coins: data.prize_coins,
                  total_chests: data.total_chests,
                  chests_opened: data.chests_opened,
                  chests_remaining: data.chests_remaining,
                  active: data.active !== undefined ? data.active : prev?.active,
                  // IMPORTANTE: Atualiza winners se presente
                  winners: data.winners || prev?.winners,
                  // Se novo baú, limpar last_winner para não confundir
                  last_winner: newChestAvailable ? null : (preserveLastWinner ? prev.last_winner : (data.last_winner || prev?.last_winner))
                };
              });
              
              // Atualiza usuários online se presente
              if (data.online_users !== undefined) {
                setOnlineUsers(data.online_users || []);
              }
            }
            
            // Atualização específica de usuários online
            if (data.type === 'online_users_update') {
              setOnlineUsers(data.users || []);
            }
            
            // ===== CHAT AO VIVO =====
            if (data.type === 'initial_status' && data.chat_history !== undefined) {
              const welcomeMessage = {
                id: 'welcome-' + Date.now(),
                type: 'system',
                message: '📢 Bem-vindo ao Chat!\n• Respeite todos os membros\n• Proibido bullying e ofensas\n• Divirta-se e boa sorte! 🍀',
                timestamp: new Date().toISOString()
              };
              setChatMessages([...(data.chat_history || []), welcomeMessage]);
            }
            
            // Nova mensagem de chat
            if (data.type === 'chat_message' && data.message) {
              setChatMessages(prev => {
                const exists = prev.some(msg => msg.id === data.message.id);
                if (exists) return prev;
                return [...prev.slice(-49), data.message];
              });
            }
            
            // Erro de chat
            if (data.type === 'chat_error') {
              setChatError(data.message || 'Erro ao enviar mensagem');
              setTimeout(() => setChatError(null), 3000);
            }
            
            // 🔐 SESSÃO SUBSTITUÍDA (outro dispositivo conectou)
            if (data.type === 'session_replaced') {
              console.log('🔐 Sessão substituída por outro dispositivo');
              setSessionReplaced(true);
              setWsConnected(false);
              toast.error('Sua sessão foi encerrada. Você conectou em outro dispositivo.', { duration: 10000 });
              // NÃO reconectar automaticamente
              return;
            }
            
            // 🎉 ANÚNCIO DE GANHADOR(ES)
            if (data.type === 'winner_announcement') {
              // Suporte para múltiplos ganhadores
              const winners = data.winners || (data.winner ? [data.winner] : []);
              const winner = winners[0]; // Primeiro ganhador para compatibilidade
              const isCurrentUserWinner = winners.some(w => w.kako_id === kakoId);
              const currentUserPrize = winners.find(w => w.kako_id === kakoId);
              const currentResult = jackpotResultRef.current;
              
              // Limpar timeout de segurança
              if (claimTimeoutRef.current) {
                clearTimeout(claimTimeoutRef.current);
                claimTimeoutRef.current = null;
              }
              
              // Se o usuário ESTAVA PARTICIPANDO ou AGUARDANDO resultado (late_participant)
              // mostra resultado imediatamente
              if (currentResult?.participating) {
                if (isCurrentUserWinner) {
                  setJackpotResult({
                    won: true,
                    winner: currentUserPrize,
                    winners: winners,
                    prize_coins: currentUserPrize.prize_coins
                  });
                  setShowResultModal(true);
                  toast.success(`🎉 PARABÉNS! Você ganhou ${currentUserPrize.prize_coins?.toLocaleString()} moedas!`, { duration: 10000 });
                } else {
                  setJackpotResult({
                    won: false,
                    last_winner: winner,
                    winners: winners
                  });
                  setShowResultModal(true);
                }
                setJackpotClaiming(false);
                isJackpotProcessingRef.current = false;
                
                // Usuário participou - muda para cooldown
                setJackpot(prev => {
                  const chestNumber = data.chest_number || ((prev?.chests_opened || 0) + 1);
                  const newWinners = winners.map((w, idx) => ({
                    chest_number: chestNumber,
                    winner_index: idx,
                    kako_id: w.kako_id,
                    kako_nickname: w.kako_nickname,
                    kako_avatar: w.kako_avatar,
                    prize_coins: w.prize_coins,
                    won_at: new Date().toISOString()
                  }));
                  const updatedWinners = [...(prev?.winners || []), ...newWinners];
                  
                  return {
                    ...prev,
                    status: 'cooldown',
                    progress_percent: 0,
                    chests_opened: (prev?.chests_opened || 0) + 1,
                    chests_remaining: data.chests_remaining ?? (prev?.chests_remaining - 1),
                    last_winner: winner,
                    last_winners: winners,
                    winners: updatedWinners
                  };
                });
                
                // Iniciar cooldown
                if (data.cooldown_seconds) {
                  setJackpotCooldown(data.cooldown_seconds);
                }
              } else if (jackpotClaiming || isJackpotProcessingRef.current) {
                // Usuário estava no meio de uma tentativa mas não foi marcado como participating
                // Isso pode acontecer em race conditions - mostrar resultado mesmo assim
                setJackpotResult({
                  won: false,
                  last_winner: winner,
                  winners: winners
                });
                setShowResultModal(true);
                setJackpotClaiming(false);
                isJackpotProcessingRef.current = false;
                
                // Usuário participou - muda para cooldown
                setJackpot(prev => {
                  const chestNumber = data.chest_number || ((prev?.chests_opened || 0) + 1);
                  const newWinners = winners.map((w, idx) => ({
                    chest_number: chestNumber,
                    winner_index: idx,
                    kako_id: w.kako_id,
                    kako_nickname: w.kako_nickname,
                    kako_avatar: w.kako_avatar,
                    prize_coins: w.prize_coins,
                    won_at: new Date().toISOString()
                  }));
                  const updatedWinners = [...(prev?.winners || []), ...newWinners];
                  
                  return {
                    ...prev,
                    status: 'cooldown',
                    progress_percent: 0,
                    chests_opened: (prev?.chests_opened || 0) + 1,
                    chests_remaining: data.chests_remaining ?? (prev?.chests_remaining - 1),
                    last_winner: winner,
                    last_winners: winners,
                    winners: updatedWinners
                  };
                });
                
                // Iniciar cooldown
                if (data.cooldown_seconds) {
                  setJackpotCooldown(data.cooldown_seconds);
                }
              } else {
                // ✅ Usuário NÃO participou - mantém baú disponível para ele clicar
                // Apenas atualiza o histórico de ganhadores, mas NÃO muda status
                // O baú continua visível até ele clicar ou cooldown acabar
                setJackpot(prev => {
                  const chestNumber = data.chest_number || ((prev?.chests_opened || 0) + 1);
                  const newWinners = winners.map((w, idx) => ({
                    chest_number: chestNumber,
                    winner_index: idx,
                    kako_id: w.kako_id,
                    kako_nickname: w.kako_nickname,
                    kako_avatar: w.kako_avatar,
                    prize_coins: w.prize_coins,
                    won_at: new Date().toISOString()
                  }));
                  const updatedWinners = [...(prev?.winners || []), ...newWinners];
                  
                  return {
                    ...prev,
                    // NÃO muda status para cooldown - mantém 'available'
                    // Permite que usuário clique e receba pontos de sorte
                    last_winner: winner,
                    last_winners: winners,
                    winners: updatedWinners,
                    // Armazenar dados para usar depois que usuário clicar
                    _pending_chests_opened: (prev?.chests_opened || 0) + 1,
                    _pending_chests_remaining: data.chests_remaining ?? (prev?.chests_remaining - 1)
                  };
                });
                
                // ✅ IMPORTANTE: Iniciar o cooldown MESMO que usuário não participou
                // Assim o cronômetro já aparece no baú fechado, sincronizado com o servidor
                if (data.cooldown_seconds) {
                  setJackpotCooldown(data.cooldown_seconds);
                }
              }
              
              // 🍀 Atualizar sorte acumulada do usuário atual (se participou)
              if (data.luck_updates && Array.isArray(data.luck_updates)) {
                const myLuckUpdate = data.luck_updates.find(u => u.kako_id === kakoId);
                if (myLuckUpdate) {
                  setUserLuck({ luck: myLuckUpdate.luck, level: myLuckUpdate.level });
                }
              }
            }
            
            // 🍀 ATUALIZAÇÃO DE SORTE (recebida após identify)
            if (data.type === 'luck_update') {
              setUserLuck({ luck: data.luck, level: data.level });
            }
          } catch (e) {
            console.error('Erro ao processar mensagem WS:', e);
          }
        };
        
        ws.onclose = (event) => {
          console.log('🔌 Jackpot WebSocket desconectado', event.code);
          wsRef.current = null;
          setWsConnected(false);
          
          // Limpa ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          // 🔐 Se sessão foi substituída (código 4001), NÃO reconectar
          if (event.code === 4001) {
            console.log('🔐 Sessão substituída - não reconectar');
            setSessionReplaced(true);
            return;
          }
          
          // Reconecta se ainda estiver na aba e não for fechamento intencional
          if (activeTab === 'jackpot' && jackpot?.active && event.code !== 1000) {
            // Backoff exponencial: 1s, 2s, 4s, 8s, máx 30s
            const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts.current), 30000);
            wsReconnectAttempts.current++;
            console.log(`🔄 Reconectando em ${delay/1000}s (tentativa ${wsReconnectAttempts.current})`);
            wsReconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Erro no WebSocket:', error);
          setWsConnected(false);
        };
        
        // Ping a cada 25 segundos para manter conexão viva
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
        
      } catch (error) {
        console.error('Erro ao conectar WebSocket:', error);
        setWsConnected(false);
      }
    };
    
    connectWebSocket();
    
    // 📱 Reconexão quando usuário volta ao app (visibilitychange)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 App voltou ao foco');
        // Se WebSocket não está conectado, reconecta
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('🔄 Reconectando WebSocket após retorno ao app');
          connectWebSocket();
        }
        // Recarrega dados para sincronizar estado
        loadData();
      }
    };
    
    // 🌐 Reconexão quando volta online
    const handleOnline = () => {
      console.log('🌐 Conexão de internet restaurada');
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        connectWebSocket();
      }
      loadData();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        // 🍀 Resetar sorte acumulada quando sai da sala INTENCIONALMENTE
        if (kakoId) {
          // Usar fetch síncrono para resetar sorte antes de fechar
          fetch(`${BACKEND_URL}/api/mystery-box/jackpot/luck/reset/${kakoId}`, { method: 'POST' })
            .catch(() => {}); // Ignora erros silenciosamente
          setUserLuck({ luck: 0, level: 'baixa' });
        }
        wsRef.current.close(1000); // Fechamento intencional
        wsRef.current = null;
      }
      setWsConnected(false);
    };
  }, [jackpot?.active, activeTab, kakoId, progress?.kako_nickname, progress?.kako_avatar, loadData]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeMinSec = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Ref para prevenir múltiplos cliques (isProcessingRef para baús normais)
  const isProcessingRef = React.useRef(false);

  // Função para resetar estados do jackpot (usada em timeouts e erros)
  const resetJackpotState = React.useCallback(() => {
    setJackpotClaiming(false);
    isJackpotProcessingRef.current = false;
    if (claimTimeoutRef.current) {
      clearTimeout(claimTimeoutRef.current);
      claimTimeoutRef.current = null;
    }
  }, []);

  // Função para tentar abrir o Jackpot - OTIMIZADA PARA MOBILE
  const claimJackpot = async () => {
    const now = Date.now();
    
    // PROTEÇÃO 1: Debounce de 500ms para evitar cliques acidentais duplos
    if (now - lastClaimAttemptRef.current < 500) {
      console.log('🛡️ Clique ignorado (debounce 500ms)');
      return;
    }
    lastClaimAttemptRef.current = now;
    
    // PROTEÇÃO 2: Se já está processando, ignorar
    if (isJackpotProcessingRef.current) {
      console.log('🛡️ Clique ignorado (já processando)');
      return;
    }
    
    // PROTEÇÃO 3: Se já tem resultado final, não faz nada
    if (jackpotResult && !jackpotResult.participating) {
      console.log('🛡️ Clique ignorado (já tem resultado)');
      return;
    }
    
    // PROTEÇÃO 4: Se já está "claiming", verifica se travou (mais de 15s)
    if (jackpotClaiming) {
      // Se travou por mais de 15 segundos, resetar e tentar novamente
      console.log('⚠️ Estado "claiming" ativo - verificando se travou...');
      return;
    }
    
    // PROTEÇÃO 5: Se o status não é available, verificar se há ganhador
    if (jackpot?.status === 'cooldown' && jackpot?.last_winner) {
      const isCurrentUser = jackpot.last_winner.kako_id === kakoId;
      setJackpotResult({
        won: isCurrentUser,
        winner: isCurrentUser ? jackpot.last_winner : undefined,
        last_winner: !isCurrentUser ? jackpot.last_winner : undefined,
        prize_coins: jackpot.last_winner.prize_coins
      });
      setShowResultModal(true);
      if (isCurrentUser) {
        toast.success(`🎉 PARABÉNS! Você ganhou ${jackpot.last_winner.prize_coins?.toLocaleString()} moedas!`, { duration: 10000 });
      }
      return;
    }
    
    // Verificar se pode tentar - permite AVAILABLE (janela aberta ou processing)
    // Durante cooldown o botão não aparece, então não precisa verificar aqui
    if (jackpot?.status !== 'available') {
      // Se está em waiting, não pode clicar
      if (jackpot?.status === 'waiting') {
        toast.error('Baú ainda não liberou! Aguarde atingir 100%');
        return;
      }
      // Se está em cooldown mas não tem last_winner ainda, pode ser race condition
      // Deixa tentar e o backend vai responder apropriadamente
      if (jackpot?.status === 'cooldown' && !jackpot?.last_winner) {
        console.log('⚠️ Status cooldown mas sem last_winner - tentando mesmo assim...');
      } else if (jackpot?.status === 'cooldown') {
        toast.error('Baú já foi aberto! Aguarde o próximo.');
        return;
      }
    }
    
    // ===== INICIAR TENTATIVA =====
    console.log('🎰 Iniciando tentativa de claim...');
    isJackpotProcessingRef.current = true;
    setJackpotClaiming(true);
    setJackpotResult(null);
    
    // Timeout de segurança: resetar após 12 segundos se não houver resposta
    claimTimeoutRef.current = setTimeout(() => {
      const currentRes = jackpotResultRef.current;
      if (currentRes?.participating || jackpotClaiming) {
        console.log('⏰ Timeout de segurança (12s): resetando estado');
        resetJackpotState();
        setJackpotResult(null);
        // Recarregar dados para sincronizar estado
        loadData();
      }
    }, 12000);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/mystery-box/jackpot/claim?kako_id=${kakoId}&kako_nickname=${encodeURIComponent(progress?.kako_nickname || '')}&kako_avatar=${encodeURIComponent(progress?.kako_avatar || '')}`,
        {},
        { timeout: 8000 } // Timeout de 8 segundos para a request
      );
      
      if (response.data.success) {
        if (response.data.status === 'participating') {
          // Entrou na disputa - aguardar resultado via WebSocket
          setJackpotResult({
            participating: true,
            message: response.data.message,
            participants_count: response.data.participants_count
          });
          console.log('✅ Participando! Aguardando resultado via WebSocket...');
          // O resultado virá via WebSocket (winner_announcement)
        } else if (response.data.status === 'late_participant') {
          // Late participant - clicou após janela, recebeu +5% sorte
          clearTimeout(claimTimeoutRef.current);
          console.log('🍀 Late participant! Sorte atualizada:', response.data.luck_update);
          
          // Atualizar sorte local
          if (response.data.luck_update) {
            setUserLuck(response.data.luck_update);
          }
          
          // Mostrar resultado do ganhador - suporta múltiplos ganhadores
          const lastWinners = response.data.last_winners || (response.data.last_winner ? [response.data.last_winner] : []);
          const lastWinner = lastWinners[0];
          const isWinner = lastWinners.some(w => w?.kako_id === kakoId);
          setJackpotResult({
            won: isWinner,
            late_participant: true,
            last_winner: lastWinner,
            winners: lastWinners,
            luck_updated: true
          });
          setShowResultModal(true);
          toast.success('🍀 +5% de sorte acumulada!', { duration: 3000 });
          resetJackpotState();
          
          // ✅ Agora que usuário clicou, atualizar estado para cooldown
          // O cooldown JÁ está rodando (iniciado no winner_announcement)
          setJackpot(prev => ({
            ...prev,
            status: 'cooldown',
            progress_percent: 0,
            chests_opened: prev?._pending_chests_opened ?? (prev?.chests_opened || 0) + 1,
            chests_remaining: prev?._pending_chests_remaining ?? (prev?.chests_remaining - 1),
            _pending_chests_opened: undefined,
            _pending_chests_remaining: undefined
          }));
          // NÃO reiniciar cooldown - já está rodando
        } else if (response.data.status === 'already_clicked') {
          // Já clicou neste baú - apenas mostrar resultado
          clearTimeout(claimTimeoutRef.current);
          console.log('⚠️ Já clicou neste baú');
          
          // Suporta múltiplos ganhadores
          const lastWinners = response.data.last_winners || (response.data.last_winner ? [response.data.last_winner] : []);
          const lastWinner = lastWinners[0];
          const isWinner = lastWinners.some(w => w?.kako_id === kakoId);
          setJackpotResult({
            won: isWinner,
            late_participant: true,
            last_winner: lastWinner,
            winners: lastWinners
          });
          setShowResultModal(true);
          resetJackpotState();
          
          // ✅ Atualizar estado para cooldown
          // O cooldown JÁ está rodando
          setJackpot(prev => ({
            ...prev,
            status: 'cooldown',
            progress_percent: 0,
            chests_opened: prev?._pending_chests_opened ?? (prev?.chests_opened || 0) + 1,
            chests_remaining: prev?._pending_chests_remaining ?? (prev?.chests_remaining - 1),
            _pending_chests_opened: undefined,
            _pending_chests_remaining: undefined
          }));
          // NÃO reiniciar cooldown - já está rodando
        } else {
          // Resposta de vitória direta (raro)
          clearTimeout(claimTimeoutRef.current);
          setJackpotResult({
            won: true,
            winner: response.data.winner,
            prize_coins: response.data.prize_coins
          });
          toast.success(`🎉 PARABÉNS! Você ganhou ${response.data.prize_coins?.toLocaleString()} moedas!`, { duration: 10000 });
          setJackpotCooldown(response.data.cooldown_seconds || 300);
          setShowResultModal(true);
          resetJackpotState();
        }
      } else {
        // Não conseguiu participar
        const errorType = response.data.error;
        console.log(`⚠️ Resposta não-sucesso: ${errorType}`);
        
        if (errorType === 'processing') {
          // Sorteio em andamento - mostrar spinner e aguardar WebSocket
          setJackpotResult({
            participating: true,
            message: response.data.message || 'Aguardando resultado...'
          });
        } else if (response.data.last_winner) {
          // Já tem ganhador
          clearTimeout(claimTimeoutRef.current);
          const isWinner = response.data.last_winner.kako_id === kakoId;
          setJackpotResult({
            won: isWinner,
            error: errorType,
            winner: isWinner ? response.data.last_winner : undefined,
            last_winner: response.data.last_winner
          });
          setShowResultModal(true);
          resetJackpotState();
        } else {
          // Erro genérico
          clearTimeout(claimTimeoutRef.current);
          toast.error(response.data.message || 'Não foi possível participar');
          resetJackpotState();
        }
      }
      
    } catch (error) {
      console.error('❌ Erro ao abrir jackpot:', error);
      clearTimeout(claimTimeoutRef.current);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Conexão lenta. Tente novamente!');
      } else if (error.response?.data?.last_winner) {
        const isWinner = error.response.data.last_winner.kako_id === kakoId;
        setJackpotResult({
          won: isWinner,
          error: error.response.data.error,
          last_winner: error.response.data.last_winner
        });
        setShowResultModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Erro ao abrir o baú. Tente novamente!');
      }
      resetJackpotState();
    }
  };

  // ===== CHAT AO VIVO =====
  const sendChatMessage = () => {
    // Proteção contra duplo clique
    if (chatSending) return;
    
    const messageText = chatInput.trim();
    if (!messageText || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    
    // Anti-duplicação: não enviar mesma mensagem consecutiva
    if (messageText === lastSentMessageRef.current) {
      setChatInput(''); // Limpa o input mas não envia
      return;
    }
    
    setChatSending(true); // Bloqueia envio
    lastSentMessageRef.current = messageText; // Registra última mensagem
    
    // Envia via WebSocket
    wsRef.current.send(JSON.stringify({
      type: 'chat_message',
      kako_id: kakoId,
      kako_nickname: progress?.kako_nickname || '',
      kako_avatar: progress?.kako_avatar || '',
      message: messageText
    }));
    
    setChatInput(''); // Limpa o input
    
    // Libera após 1 segundo (tempo mínimo entre envios)
    setTimeout(() => setChatSending(false), 1000);
    
    // Limpa referência de última mensagem após 5 segundos (permite reenviar depois)
    setTimeout(() => {
      if (lastSentMessageRef.current === messageText) {
        lastSentMessageRef.current = '';
      }
    }, 5000);
  };
  
  // Handler para Enter no input
  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const openBox = async () => {
    // PROTEÇÃO: Prevenir múltiplos cliques
    if (isProcessingRef.current || opening) {
      return;
    }
    
    isProcessingRef.current = true;
    setOpening(true);
    setIsShaking(true);
    setIsOpen(false);
    setShowPrize(false);
    setPrizeAmount(null);
    
    try {
      // Fase 1: Shake (1.5s)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Fase 2: Chamar API
      const response = await axios.post(`${BACKEND_URL}/api/mystery-box/open/${kakoId}`);
      
      // Fase 3: Parar shake e abrir tampa
      setIsShaking(false);
      await new Promise(resolve => setTimeout(resolve, 100));
      setIsOpen(true);
      
      // Fase 4: Mostrar prêmio após tampa abrir
      await new Promise(resolve => setTimeout(resolve, 600));
      setPrizeAmount(response.data.won ? response.data.points_won : 0);
      setPrizeName(response.data.won ? response.data.prize_name : null);
      setPrizeImageUrl(response.data.won ? response.data.prize_image_url : null);
      setShowPrize(true);
      
      // Notificação
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (response.data.already_opened) {
        toast.info('Este baú já foi aberto!', { duration: 3000 });
      } else if (response.data.won) {
        toast.success(`Você ganhou ${response.data.prize_name}! +${response.data.points_won} pontos!`, {
          duration: 5000,
          icon: '🎉'
        });
      } else {
        toast.info('O baú estava vazio. Tente novamente!', {
          duration: 3000,
          icon: '😢'
        });
      }
      
      // Recarregar dados
      const newData = await loadData();
      
      // ✅ Se ainda tem baús disponíveis, resetar estado visual após 2 segundos
      // para mostrar o próximo baú
      if (newData?.progress?.pending_boxes > 0) {
        setTimeout(() => {
          resetForNextBox();
        }, 2000); // Aguarda 2 segundos para usuário ver o resultado
      }
    } catch (error) {
      console.error('Erro ao abrir baú:', error);
      setIsShaking(false);
      
      if (error.response?.status === 404) {
        toast.info('Baú não disponível', { duration: 3000 });
        loadData();
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao abrir baú');
      }
    } finally {
      setOpening(false);
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000);
    }
  };

  const resetForNextBox = () => {
    setIsOpen(false);
    setShowPrize(false);
    setPrizeAmount(null);
    setPrizeName(null);
    setPrizeImageUrl(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-b from-purple-900 via-indigo-900 to-blue-900">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  if (!progress?.active) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-4 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="text-6xl sm:text-8xl mb-6 opacity-50">📦</div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-400 mb-2">Baú Misterioso</h2>
        <p className="text-gray-500 text-sm sm:text-base">O evento não está ativo no momento.</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-3 sm:p-4 relative overflow-hidden"
      style={{
        backgroundImage: `url(${mysteryBoxBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <AnimationStyles />
      
      {/* Overlay escuro para melhor legibilidade */}
      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
      
      {/* Background particles suaves */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        {BG_PARTICLES.map((p, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full animate-pulse"
            style={{
              left: p.left,
              top: p.top,
              animationDelay: p.delay
            }}
          />
        ))}
      </div>
      
      <div className="max-w-lg mx-auto space-y-4 sm:space-y-6 relative z-10">
        
        {/* Título - Direto sobre o fundo, sem card */}
        <div className="text-center py-4 sm:py-6">
          <img 
            src={mysteryBoxTitle} 
            alt="Baú Misterioso" 
            className="h-28 sm:h-36 md:h-44 mx-auto object-contain drop-shadow-2xl"
          />
          <p className="text-sm sm:text-base text-white drop-shadow-lg mt-3 font-bold">Recarregue e ganhe prêmios!</p>
        </div>

        {/* Abas */}
        <ClientTabs key={activeTab} activeTab={activeTab} setActiveTab={setActiveTab} jackpotActive={jackpot?.active} />

        {/* Conteúdo da Aba Jackpot */}
        {activeTab === 'jackpot' && jackpot?.active && (
          <Card className="bg-purple-900/60 backdrop-blur-lg border-purple-500/30 shadow-lg shadow-purple-500/10 overflow-hidden relative">
            {/* 🔴 OVERLAY DE DESCONEXÃO - Cobre todo o card quando desconectado */}
            {(!wsConnected || sessionReplaced) && (
              <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6 rounded-xl">
                {sessionReplaced ? (
                  // Sessão substituída por outro dispositivo
                  <>
                    <div className="text-6xl mb-4">📱</div>
                    <h3 className="text-xl font-bold text-yellow-400 text-center mb-2">
                      Conectado em outro dispositivo
                    </h3>
                    <p className="text-gray-300 text-center text-sm mb-4">
                      Sua sessão foi encerrada porque você acessou de outro lugar.
                    </p>
                    <button
                      onClick={() => {
                        setSessionReplaced(false);
                        setActiveTab('event');
                        // Scroll para o topo para garantir que o conteúdo seja visível
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold px-6 py-3 rounded-xl transition-all flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Voltar
                    </button>
                  </>
                ) : (
                  // Conexão perdida - tentando reconectar
                  <>
                    <div className="relative mb-4">
                      <div className="text-6xl">📡</div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center animate-pulse">
                        <span className="text-white text-xs">!</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-purple-400 text-center mb-2">
                      Conexão Perdida
                    </h3>
                    <p className="text-gray-300 text-center text-sm mb-4">
                      Tentando reconectar...
                    </p>
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-purple-400" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      <span className="text-purple-400 font-medium animate-pulse">Reconectando...</span>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Header com efeito brilhante */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" />
            
            <CardHeader className="pb-2 px-4 pt-4 relative">
              {/* Botões - Canto superior direito */}
              <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                {/* Botão de Som */}
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`p-1.5 rounded-lg transition-all border ${
                    soundEnabled 
                      ? 'bg-yellow-500/80 hover:bg-yellow-400 text-purple-900 border-yellow-400/50' 
                      : 'bg-purple-700/80 hover:bg-purple-600 text-gray-400 border-purple-500/50'
                  }`}
                  title={soundEnabled ? 'Som ativado' : 'Som desativado'}
                  data-testid="jackpot-sound-btn"
                >
                  {soundEnabled ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  )}
                </button>
                
                {/* Botão de Regras */}
                <button
                  onClick={() => setShowRulesModal(true)}
                  className="bg-purple-700/80 hover:bg-purple-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 border border-purple-500/50"
                  data-testid="jackpot-rules-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Regras
                </button>
              </div>
              
              <CardTitle className="text-center">
                <img 
                  src={require('../assets/images/bau_jackpot_title.png')} 
                  alt="Baú Jackpot" 
                  className="w-48 sm:w-56 mx-auto"
                />
              </CardTitle>
            </CardHeader>
            
            <CardContent className="px-4 pb-4 space-y-4 relative">
              {/* 🍀 Velocímetro de Sorte - Acima da Barra de Progresso */}
              <LuckSpeedometer luck={userLuck.luck} level={userLuck.level} />
              
              {/* Barra de Progresso */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-purple-300">Progresso</span>
                  <span className="text-yellow-300 font-bold text-lg">
                    {jackpot?.progress_percent?.toFixed(2) || 0}%
                  </span>
                </div>
                <div className="h-4 bg-purple-950/80 rounded-full overflow-hidden border border-purple-500/30">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-500 transition-all duration-500 relative"
                    style={{ width: `${Math.min(100, jackpot?.progress_percent || 0)}%` }}
                  >
                    {/* Efeito de brilho na barra */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                  </div>
                </div>
              </div>
              
              {/* Info do Prêmio */}
              <div className="flex justify-between items-center p-3 bg-black/30 rounded-xl border border-purple-500/30">
                <div>
                  <p className="text-purple-300 text-xs">Prêmio</p>
                  <p className="text-yellow-300 font-bold text-xl">{jackpot?.prize_coins?.toLocaleString() || 0} 🪙</p>
                </div>
                <div className="text-right">
                  <p className="text-purple-300 text-xs">Baús Restantes</p>
                  <p className="text-white font-bold text-xl">{jackpot?.chests_remaining || 0}/{jackpot?.total_chests || 0}</p>
                </div>
              </div>
              
              {/* Info da Sequência Atual (se houver) */}
              {jackpot?.sequence_info && (
                <div className="p-2 bg-gradient-to-r from-orange-900/50 to-purple-900/50 rounded-xl border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-300 text-xs">📋</span>
                      <span className="text-orange-200 text-sm font-medium">
                        {jackpot.sequence_info.current_sequence_name}
                      </span>
                    </div>
                    <span className="text-orange-300 text-xs">
                      Fase {jackpot.sequence_info.current_index + 1} de {jackpot.sequence_info.total_sequences}
                    </span>
                  </div>
                  <div className="mt-1.5 flex justify-between text-xs">
                    <span className="text-purple-300">
                      Baús da fase: {jackpot.sequence_info.sequence_chests_opened}/{jackpot.sequence_info.sequence_chests_total}
                    </span>
                    <span className="text-yellow-300">
                      Restam: {jackpot.sequence_info.sequence_chests_remaining}
                    </span>
                  </div>
                  <div className="mt-1 h-1 bg-black/30 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-400 to-yellow-400 transition-all duration-500"
                      style={{ 
                        width: `${jackpot.sequence_info.sequence_chests_total > 0 
                          ? (jackpot.sequence_info.sequence_chests_opened / jackpot.sequence_info.sequence_chests_total) * 100 
                          : 0}%` 
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Baú Visual e Botão */}
              <div className="relative py-6 flex flex-col items-center">
                {/* Status do Baú */}
                {jackpot?.status === 'waiting' && (
                  <>
                    <div className="text-6xl opacity-50 grayscale mb-3">🔒</div>
                    <p className="text-purple-300 text-sm text-center">
                      Aguarde atingir <span className="text-yellow-300 font-bold">100%</span> para liberar o baú
                    </p>
                  </>
                )}
                
                {/* Estado: Baú disponível - SOMENTE quando disponível E não está em transição/reset */}
                {jackpot?.status === 'available' && !jackpotClaiming && !jackpotResult?.participating && (
                  <>
                    <div className="text-7xl animate-bounce mb-3">🎁</div>
                    <p className="text-yellow-300 text-lg font-bold mb-2 animate-pulse">
                      BAÚ LIBERADO!
                    </p>
                    
                    {/* 🆕 Countdown URGENTE - tempo para clicar antes de perder */}
                    {jackpotCooldown > 0 && (
                      <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 mb-3">
                        <p className="text-red-300 text-xs mb-1">⚠️ CLIQUE ANTES DE ACABAR!</p>
                        <p className="text-red-400 text-2xl font-mono font-bold">
                          {formatTimeMinSec(jackpotCooldown)}
                        </p>
                      </div>
                    )}
                    
                    <Button
                      onClick={claimJackpot}
                      disabled={jackpotClaiming}
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-yellow-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {jackpotClaiming ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          ABRINDO...
                        </span>
                      ) : (
                        '🎰 ABRIR BAÚ!'
                      )}
                    </Button>
                  </>
                )}
                
                {/* Estado: Aguardando resultado (apenas spinner) */}
                {jackpotResult?.participating && (
                  <div className="text-center">
                    <div className="text-7xl mb-4">🎁</div>
                    <div className="flex items-center justify-center mb-3">
                      <svg className="animate-spin h-10 w-10 text-yellow-400" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                    </div>
                    <p className="text-yellow-300 text-lg font-bold animate-pulse">
                      ABRINDO...
                    </p>
                  </div>
                )}
                
                {/* Estado: Cooldown - mostra para TODOS (participou ou não) */}
                {jackpot?.status === 'cooldown' && !jackpotResult?.participating && (
                  <>
                    <div className="text-6xl mb-3">⏳</div>
                    <p className="text-purple-300 text-sm mb-2">Próximo baú em:</p>
                    <p className="text-yellow-300 text-3xl font-mono font-bold">
                      {formatTimeMinSec(jackpotCooldown)}
                    </p>
                  </>
                )}
                
                {/* Estado: Processando reset/transição - mostrar cadeado */}
                {jackpotClaiming && !jackpotResult?.participating && (
                  <>
                    <div className="text-6xl opacity-50 grayscale mb-3">🔒</div>
                    <p className="text-purple-300 text-sm text-center animate-pulse">
                      Processando...
                    </p>
                  </>
                )}
                
                {jackpot?.status === 'finished' && (
                  <>
                    <div className="text-6xl mb-3">🏁</div>
                    <p className="text-purple-300 text-lg font-bold">Evento Finalizado!</p>
                    <p className="text-gray-400 text-sm">Todos os baús foram abertos</p>
                  </>
                )}
              </div>
              
              {/* ===== CHAT AO VIVO COM USUÁRIOS ONLINE ===== */}
              <div className="border-t border-purple-500/30 pt-4 mt-4">
                {/* Header do Chat com Usuários Online */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className="flex items-center gap-2 text-purple-300 text-sm font-medium hover:text-yellow-300 transition-colors"
                  >
                    <span>💬 Chat ao Vivo</span>
                    {/* Indicador de Conexão WiFi */}
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      sessionReplaced
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : wsConnected 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-purple-500/20 text-purple-400 animate-pulse'
                    }`}>
                      {sessionReplaced ? (
                        <>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                          <span className="hidden sm:inline">Outro dispositivo</span>
                        </>
                      ) : wsConnected ? (
                        <>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 3C7.46 3 3.34 4.78.29 7.67c-.18.18-.29.43-.29.71 0 .28.11.53.29.71.18.18.43.29.71.29.28 0 .53-.11.71-.29C4.03 6.86 7.8 5.5 12 5.5s7.97 1.36 10.29 3.59c.18.18.43.29.71.29.28 0 .53-.11.71-.29.18-.18.29-.43.29-.71 0-.28-.11-.53-.29-.71C20.66 4.78 16.54 3 12 3zM12 7c-3.31 0-6.28 1.34-8.48 3.52-.18.18-.29.43-.29.71 0 .28.11.53.29.71.18.18.43.29.71.29.28 0 .53-.11.71-.29C6.74 10.14 9.24 9 12 9s5.26 1.14 7.06 2.94c.18.18.43.29.71.29.28 0 .53-.11.71-.29.18-.18.29-.43.29-.71 0-.28-.11-.53-.29-.71C18.28 8.34 15.31 7 12 7zm0 4c-2.21 0-4.21.9-5.66 2.34-.18.18-.29.43-.29.71 0 .28.11.53.29.71.18.18.43.29.71.29.28 0 .53-.11.71-.29C8.72 13.8 10.27 13 12 13s3.28.8 4.24 1.76c.18.18.43.29.71.29.28 0 .53-.11.71-.29.18-.18.29-.43.29-.71 0-.28-.11-.53-.29-.71C16.21 11.9 14.21 11 12 11zm0 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                        <span className="hidden sm:inline">Conectado</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21.71 7.67C18.66 4.78 14.54 3 10 3c-1.4 0-2.74.17-4.02.48l2.73 2.73C9.77 6.08 10.87 6 12 6c4.2 0 7.97 1.36 10.29 3.59.18.18.43.29.71.29.28 0 .53-.11.71-.29.18-.18.29-.43.29-.71 0-.28-.11-.53-.29-.71zM3.41 1.59L2 3l2.05 2.05C1.91 6.68.29 8.95.29 8.95c-.18.18-.29.43-.29.71s.11.53.29.71c.18.18.43.29.71.29s.53-.11.71-.29S3.82 8.24 5.61 6.61L7.2 8.2C5.36 9.34 3.94 10.93 3.29 12.71c-.08.21-.12.43-.12.66 0 .28.11.53.29.71.18.18.43.29.71.29.47 0 .86-.28 1.02-.71.63-1.7 2.01-3.07 3.71-3.7L10.5 11.5c-1.63.36-3.01 1.37-3.84 2.74-.18.3-.26.64-.18.97.08.33.28.62.56.8.19.12.4.19.63.19.48 0 .92-.26 1.15-.68.58-.96 1.61-1.52 2.68-1.52.34 0 .67.06.98.17l2.02 2.02c-.98-.1-1.98.12-2.83.65-.42.26-.68.71-.68 1.19 0 .76.61 1.38 1.38 1.38.19 0 .38-.04.55-.12.53-.26 1.14-.26 1.67 0 .17.08.36.12.55.12.06 0 .12 0 .17-.01l1.44 1.44.01-.01L21 21l1.41-1.41L3.41 1.59z"/>
                        </svg>
                        <span className="hidden sm:inline">Reconectando...</span>
                      </>
                    )}
                    </span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${showChat ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {/* 🌐 Toggle + Seletor de Idioma do Chat */}
                    <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-lg border border-purple-500/30 p-0.5">
                      {/* Botão Toggle Tradutor */}
                      <button
                        onClick={() => setTranslatorEnabled(!translatorEnabled)}
                        className={`p-1.5 rounded-md transition-all ${
                          translatorEnabled 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-transparent text-gray-500 hover:text-gray-300'
                        }`}
                        title={translatorEnabled ? 'Tradutor ativado' : 'Tradutor desativado'}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                        </svg>
                      </button>
                      
                      {/* Seletor de Idioma */}
                      <select
                        value={chatLang}
                        onChange={(e) => setChatLang(e.target.value)}
                        disabled={!translatorEnabled}
                        className={`appearance-none text-xs px-2 py-1 pr-5 rounded cursor-pointer focus:outline-none ${
                          translatorEnabled 
                            ? 'bg-black/80 text-gray-200 hover:text-white border border-purple-500/30' 
                            : 'bg-black/40 text-gray-600 cursor-not-allowed border border-gray-700/30'
                        }`}
                        style={{ backgroundColor: '#1a1a2e' }}
                        title={translatorEnabled ? 'Idioma de tradução' : 'Ative o tradutor primeiro'}
                      >
                        <option value="pt" style={{ backgroundColor: '#1a1a2e', color: '#e5e5e5' }}>🇧🇷 PT</option>
                        <option value="es" style={{ backgroundColor: '#1a1a2e', color: '#e5e5e5' }}>🇪🇸 ES</option>
                        <option value="en" style={{ backgroundColor: '#1a1a2e', color: '#e5e5e5' }}>🇺🇸 EN</option>
                      </select>
                      <svg 
                        className={`absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 pointer-events-none ${
                          translatorEnabled ? 'text-gray-400' : 'text-gray-600'
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      {isTranslating && translatorEnabled && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                      )}
                    </div>
                    
                    {/* Bolha de Usuários Online (movida do topo direito) */}
                    <div className="relative">
                      <button
                        onClick={() => setShowOnlineUsers(!showOnlineUsers)}
                        className="flex items-center gap-1.5 bg-black/40 hover:bg-black/60 backdrop-blur-sm px-2.5 py-1.5 rounded-full border border-green-500/30 transition-all"
                      >
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-green-400 text-xs font-medium">{onlineUsers.length}</span>
                        <svg 
                          className={`w-3 h-3 text-green-400 transition-transform ${showOnlineUsers ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    
                    {/* Lista de Usuários Online */}
                    {showOnlineUsers && (
                      <div className="absolute bottom-full right-0 mb-2 w-56 max-h-64 overflow-y-auto bg-black/90 backdrop-blur-lg rounded-xl border border-green-500/30 shadow-xl z-20">
                        <div className="p-2 border-b border-green-500/20">
                          <p className="text-green-400 text-xs font-medium text-center">
                            {onlineUsers.length} usuário{onlineUsers.length !== 1 ? 's' : ''} aguardando
                          </p>
                        </div>
                        <div className="p-1.5 space-y-1">
                          {onlineUsers.length === 0 ? (
                            <p className="text-gray-400 text-xs text-center py-2">Nenhum usuário online</p>
                          ) : (
                            onlineUsers.map((user, idx) => (
                              <div 
                                key={user.kako_id || idx}
                                className={`flex items-center gap-2 p-1.5 rounded-lg ${
                                  user.kako_id === kakoId ? 'bg-yellow-500/20 border border-yellow-500/30' : 'hover:bg-white/5'
                                }`}
                              >
                                <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                                  {user.kako_avatar ? (
                                    <img src={user.kako_avatar} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs">👤</div>
                                  )}
                                </div>
                                <span className={`text-xs truncate ${user.kako_id === kakoId ? 'text-yellow-300 font-medium' : 'text-gray-300'}`}>
                                  {user.kako_nickname || 'Anônimo'}
                                  {user.kako_id === kakoId && ' (você)'}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
                
                {showChat && (
                  <div className="space-y-3">
                    {/* Área de Mensagens */}
                    <div 
                      ref={chatContainerRef}
                      className="h-48 overflow-y-auto bg-black/30 rounded-xl p-3 space-y-2 scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-transparent"
                    >
                      {chatMessages.length === 0 ? (
                        <p className="text-gray-400 text-xs text-center py-4">
                          Nenhuma mensagem ainda. Seja o primeiro a falar!
                        </p>
                      ) : (
                        chatMessages.map((msg) => {
                          // Função para formatar horário de Brasília
                          const formatBrasiliaTime = (timestamp) => {
                            if (!timestamp) return '';
                            const date = new Date(timestamp);
                            return date.toLocaleTimeString('pt-BR', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              timeZone: 'America/Sao_Paulo'
                            });
                          };
                          
                          // Mensagem de sistema (boas-vindas)
                          if (msg.type === 'system') {
                            return (
                              <div 
                                key={msg.id}
                                className="bg-blue-500/20 border border-blue-400/30 rounded-xl px-3 py-2 text-blue-200 text-xs whitespace-pre-line"
                              >
                                {getTranslatedMessage(msg)}
                                {isMessageTranslated(msg) && (
                                  <span className="text-blue-400/60 text-[9px] ml-1">🌐</span>
                                )}
                              </div>
                            );
                          }
                          
                          // Mensagem normal de usuário
                          const colors = [
                            { bg: 'from-pink-500/30 to-purple-500/30', nick: 'text-pink-300' },
                            { bg: 'from-blue-500/30 to-cyan-500/30', nick: 'text-cyan-300' },
                            { bg: 'from-green-500/30 to-emerald-500/30', nick: 'text-green-300' },
                            { bg: 'from-orange-500/30 to-red-500/30', nick: 'text-orange-300' },
                            { bg: 'from-yellow-500/30 to-amber-500/30', nick: 'text-yellow-300' },
                            { bg: 'from-indigo-500/30 to-violet-500/30', nick: 'text-violet-300' },
                            { bg: 'from-teal-500/30 to-green-500/30', nick: 'text-teal-300' },
                            { bg: 'from-rose-500/30 to-pink-500/30', nick: 'text-rose-300' },
                          ];
                          const colorIndex = msg.kako_id ? 
                            msg.kako_id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length 
                            : 0;
                          const colorScheme = colors[colorIndex];
                          
                          return (
                            <div 
                              key={msg.id}
                              className={`flex items-center gap-2 bg-gradient-to-r ${colorScheme.bg} rounded-xl px-2 py-1.5`}
                            >
                              {/* Avatar */}
                              <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 border border-white/20">
                                {msg.kako_avatar ? (
                                  <img src={msg.kako_avatar} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-xs bg-gray-600">👤</div>
                                )}
                              </div>
                              
                              {/* Nickname: Mensagem */}
                              <p className="text-sm flex-1 min-w-0">
                                <span className={`font-bold ${colorScheme.nick}`}>{msg.kako_nickname}:</span>
                                <span className="text-white ml-1">{getTranslatedMessage(msg)}</span>
                                {isMessageTranslated(msg) && (
                                  <span className="text-purple-400/60 text-[9px] ml-1">🌐</span>
                                )}
                              </p>
                              
                              {/* Horário à direita */}
                              <span className="text-gray-400 text-[10px] flex-shrink-0">{formatBrasiliaTime(msg.timestamp)}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    {/* Erro de Chat */}
                    {chatError && (
                      <p className="text-purple-400 text-xs text-center animate-pulse">
                        {chatError}
                      </p>
                    )}
                    
                    {/* Input de Mensagem */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value.slice(0, 200))}
                        onKeyPress={handleChatKeyPress}
                        placeholder="Digite sua mensagem..."
                        maxLength={200}
                        className="flex-1 bg-black/40 border border-purple-500/30 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 transition-colors"
                      />
                      <button
                        onClick={sendChatMessage}
                        disabled={!chatInput.trim() || chatSending}
                        className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-purple-900 font-bold px-4 rounded-xl transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Aviso */}
                    <p className="text-gray-500 text-[10px] text-center">
                      Máximo 200 caracteres
                    </p>
                  </div>
                )}
              </div>
              
              {/* Histórico de Ganhadores - Agrupado por Dia e por Baú */}
              {jackpot?.winners?.length > 0 && (
                <div className="border-t border-purple-500/30 pt-4">
                  <p className="text-purple-300 text-sm font-medium mb-3">🏆 Histórico de Ganhadores</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {(() => {
                      // Agrupar ganhadores por dia e baú
                      const groupedByDay = {};
                      jackpot.winners.forEach(winner => {
                        // Extrair data do won_at
                        let dayKey = 'Sem data';
                        if (winner.won_at) {
                          try {
                            const date = new Date(winner.won_at);
                            dayKey = date.toLocaleDateString('pt-BR', { 
                              day: '2-digit', 
                              month: '2-digit',
                              year: 'numeric',
                              timeZone: 'America/Sao_Paulo'
                            });
                          } catch (e) {}
                        }
                        if (!groupedByDay[dayKey]) {
                          groupedByDay[dayKey] = { chests: {} };
                        }
                        // Agrupar por chest_number dentro do dia
                        const chestNum = winner.chest_number;
                        if (!groupedByDay[dayKey].chests[chestNum]) {
                          groupedByDay[dayKey].chests[chestNum] = [];
                        }
                        groupedByDay[dayKey].chests[chestNum].push(winner);
                      });
                      
                      // Ordenar dias (mais recente primeiro)
                      const sortedDays = Object.keys(groupedByDay).sort((a, b) => {
                        if (a === 'Sem data') return 1;
                        if (b === 'Sem data') return -1;
                        const [dayA, monthA, yearA] = a.split('/').map(Number);
                        const [dayB, monthB, yearB] = b.split('/').map(Number);
                        return new Date(yearB, monthB-1, dayB) - new Date(yearA, monthA-1, dayA);
                      });
                      
                      return sortedDays.map(day => (
                        <div key={day} className="bg-black/20 rounded-lg p-2">
                          {/* Cabeçalho do dia */}
                          <div className="flex items-center mb-2 pb-1 border-b border-yellow-500/20">
                            <span className="text-yellow-300 text-xs font-bold">📅 {day}</span>
                          </div>
                          {/* Baús do dia */}
                          <div className="space-y-2">
                            {Object.keys(groupedByDay[day].chests)
                              .sort((a, b) => Number(b) - Number(a))
                              .map(chestNum => {
                                const winnersInChest = groupedByDay[day].chests[chestNum];
                                const isMultiWinner = winnersInChest.length > 1;
                                
                                return (
                                  <div key={chestNum} className="bg-black/30 rounded p-1.5">
                                    {/* Header do baú */}
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-yellow-400 font-bold text-xs">#{chestNum}</span>
                                      {isMultiWinner && (
                                        <span className="text-purple-300 text-[10px]">
                                          ({winnersInChest.length} ganhadores)
                                        </span>
                                      )}
                                    </div>
                                    {/* Lista de ganhadores do baú - ordenado do maior prêmio para o menor */}
                                    <div className="space-y-1">
                                      {winnersInChest
                                        .sort((a, b) => (b.prize_coins || 0) - (a.prize_coins || 0))
                                        .map((winner, idx) => (
                                        <div key={idx} className="flex items-center gap-2 p-1 bg-black/20 rounded">
                                          <div className="w-5 h-5 rounded-full overflow-hidden border border-yellow-500/50 flex-shrink-0">
                                            {winner.kako_avatar ? (
                                              <img src={winner.kako_avatar} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full bg-yellow-600 flex items-center justify-center text-[8px]">👤</div>
                                            )}
                                          </div>
                                          <span className="text-white text-xs truncate flex-1">{winner.kako_nickname}</span>
                                          <span className="text-yellow-300 text-xs font-bold">{winner.prize_coins?.toLocaleString()} 🪙</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ));
                    })()}
                    {/* Baús restantes */}
                    {(jackpot.chests_remaining || 0) > 0 && (
                      <div className="bg-black/10 rounded-lg p-2">
                        <p className="text-gray-400 text-xs text-center">
                          ⏳ {jackpot.chests_remaining} baú(s) restante(s)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Conteúdo da Aba Evento */}
        {activeTab === 'event' && (
          <>
        {/* Cronômetro */}
        <div className="bg-purple-900/60 rounded-xl sm:rounded-2xl p-3 sm:p-5 text-center backdrop-blur-md border border-purple-500/30 shadow-lg shadow-purple-500/10">
          <p className="text-yellow-300 text-xs sm:text-sm mb-1 sm:mb-2 font-medium">⏰ Tempo restante hoje:</p>
          <p className="text-3xl sm:text-4xl font-mono font-bold text-white tracking-wider">
            {formatTime(timeRemaining)}
          </p>
          <div className="mt-2 h-1.5 bg-purple-950 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all duration-1000"
              style={{ width: `${(timeRemaining / 86400) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Card TOP 20 Ganhadores da Semana - Mega Sortudos */}
        {(topWinnersCurrent.length > 0 || topWinnersPrevious.length > 0 || myWeeklyPrizes.length > 0) && (
          <Card className="bg-purple-900/40 backdrop-blur-lg border-yellow-500/30 shadow-lg shadow-yellow-500/10">
            <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-yellow-300 text-base sm:text-lg flex items-center gap-2">
                    <span className="text-xl sm:text-2xl">🍀</span>
                    Mega Sortudos da Semana
                  </CardTitle>
                  <p className="text-purple-300 text-xs mt-1">
                    {winnersPeriod === 'current' ? weekInfo.current : weekInfo.previous}
                  </p>
                </div>
                {/* Abas Semana Atual / Semana Anterior */}
                <div className="flex gap-1 bg-purple-800/50 p-1 rounded-lg">
                  <button
                    onClick={() => { setWinnersPeriod('current'); setShowFullRanking(false); }}
                    className={`px-2 py-1 text-xs font-bold rounded transition-all ${
                      winnersPeriod === 'current'
                        ? 'bg-yellow-500 text-purple-900'
                        : 'text-purple-300 hover:text-white'
                    }`}
                  >
                    Esta Semana
                  </button>
                  <button
                    onClick={() => { setWinnersPeriod('previous'); setShowFullRanking(false); }}
                    className={`px-2 py-1 text-xs font-bold rounded transition-all ${
                      winnersPeriod === 'previous'
                        ? 'bg-yellow-500 text-purple-900'
                        : 'text-purple-300 hover:text-white'
                    }`}
                  >
                    Anterior
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {/* Prêmios para resgatar */}
              {myWeeklyPrizes.length > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/50 rounded-xl">
                  <p className="text-yellow-300 text-sm font-bold mb-2">🏆 Você tem prêmios para resgatar!</p>
                  {!localStorage.getItem('customer_token') && (
                    <p className="text-[11px] text-purple-200 bg-purple-900/60 border border-purple-500/40 rounded-lg px-2 py-1.5 mb-2" data-testid="claim-login-notice">
                      🔒 Para coletar, você precisa de uma conta verificada (grátis e rapidinho).
                    </p>
                  )}
                  {myWeeklyPrizes.map(prize => (
                    <div key={prize.id} className="flex items-center justify-between bg-purple-900/50 p-2 rounded-lg mb-2">
                      <div>
                        <p className="text-white text-sm font-bold">{prize.rank}º Lugar - Semana {prize.week_start}</p>
                        <p className="text-yellow-400 text-xs">{prize.prize_coins.toLocaleString()} moedas</p>
                        {prize.expires_at && (
                          <p className="text-red-300 text-[10px] mt-1">⏰ Expira em: {prize.expires_at.split(' ')[0]}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        data-testid={`claim-prize-${prize.id}`}
                        className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={claimingPrizeId !== null}
                        onClick={async () => {
                          if (claimingPrizeId) return; // Proteção extra - já está em processamento

                          const customerToken = localStorage.getItem('customer_token');
                          if (!customerToken) {
                            setShowLoginPrompt(true);
                            return;
                          }

                          setClaimingPrizeId(prize.id);
                          try {
                            const res = await axios.post(
                              `${BACKEND_URL}/api/mystery-box/weekly-ranking/claim/${prize.id}`,
                              null,
                              { headers: { Authorization: `Bearer ${customerToken}` } }
                            );
                            toast.success(res.data.message);
                            // Remove o prêmio da lista localmente para feedback instantâneo
                            setMyWeeklyPrizes(prev => prev.filter(p => p.id !== prize.id));
                            loadData();
                          } catch (err) {
                            if (err.response?.status === 401 || err.response?.status === 403) {
                              // sessão expirada/inválida → pede login novamente
                              setShowLoginPrompt(true);
                            } else {
                              toast.error(err.response?.data?.detail || 'Erro ao resgatar');
                            }
                          } finally {
                            setClaimingPrizeId(null);
                          }
                        }}
                      >
                        {claimingPrizeId === prize.id ? (
                          <span className="flex items-center gap-1">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                            Resgatando...
                          </span>
                        ) : 'Coletar'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Modal: conta necessária para coletar */}
              {showLoginPrompt && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="claim-login-modal"
                  onClick={() => setShowLoginPrompt(false)}>
                  <div className="w-full max-w-sm rounded-2xl border border-purple-500/40 bg-gradient-to-b from-purple-900 to-purple-950 p-5 text-center shadow-2xl"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="text-4xl mb-2">🔒</div>
                    <h3 className="text-white font-bold text-lg mb-1">Falta pouco para coletar!</h3>
                    <p className="text-purple-200 text-sm mb-4">
                      Para receber seu prêmio, você precisa de uma <b>conta verificada</b>.
                      É grátis, rapidinho, e seu prêmio fica guardado esperando você.
                    </p>
                    <a href="/conta" data-testid="claim-login-cta"
                      className="block w-full rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 py-2.5 font-bold text-purple-950 shadow-lg transition-transform hover:scale-[1.02]">
                      Criar conta / Entrar
                    </a>
                    <button onClick={() => setShowLoginPrompt(false)} data-testid="claim-login-close"
                      className="mt-2 text-xs text-purple-300 underline">
                      Agora não
                    </button>
                  </div>
                </div>
              )}

              {/* Ranking Completo */}
              {(() => {
                const winners = winnersPeriod === 'current' ? topWinnersCurrent : topWinnersPrevious;
                const userData = winnersPeriod === 'current' ? userRankingData.current : userRankingData.previous;
                
                if (winners.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <p className="text-purple-300 text-sm">
                        {winnersPeriod === 'current' ? 'Nenhum ganhador esta semana ainda' : 'Nenhum ganhador na semana anterior'}
                      </p>
                    </div>
                  );
                }
                
                // Medalha e linha do ranking: componentes estáveis definidos FORA
                // do render (RankBadge / RankingItem no topo do arquivo)
                
                // Mostrar TOP 3 em pódio ou lista completa
                if (!showFullRanking) {
                  return (
                    <>
                      {/* Pódio TOP 3 */}
                      <div className="flex justify-around items-end gap-2 mb-4">
                        {/* 2º Lugar */}
                        {winners[1] && (
                          <div className="flex flex-col items-center">
                            <div className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-gray-400 bg-gray-600 mb-1 ${winners[1].kako_id === kakoId ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-purple-900' : ''}`}>
                              {winners[1].kako_avatar ? (
                                <img src={winners[1].kako_avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-xl">👤</div>
                              )}
                              {winners[1].kako_id === kakoId && (
                                <div className="absolute -top-1 -right-1 px-1 bg-yellow-500 text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                              )}
                            </div>
                            <p className="text-gray-300 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-medium">
                              {winners[1].kako_nickname}
                            </p>
                            <p className="text-gray-400 text-xs">{winners[1].total_points.toLocaleString()} ⭐</p>
                            {weeklyPrizes[2] > 0 && (
                              <p className="text-green-400 text-[10px] font-bold">+{weeklyPrizes[2].toLocaleString()}</p>
                            )}
                            <div className="mt-1 bg-gradient-to-t from-gray-500 to-gray-400 w-16 sm:w-20 h-16 sm:h-20 rounded-t-lg flex items-center justify-center">
                              <span className="text-3xl sm:text-4xl">🥈</span>
                            </div>
                          </div>
                        )}
                        
                        {/* 1º Lugar */}
                        {winners[0] && (
                          <div className="flex flex-col items-center">
                            <div className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-3 border-yellow-400 bg-yellow-600 mb-1 shadow-lg shadow-yellow-500/50 ${winners[0].kako_id === kakoId ? 'ring-2 ring-white ring-offset-2 ring-offset-purple-900' : ''}`}>
                              {winners[0].kako_avatar ? (
                                <img src={winners[0].kako_avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-2xl">👤</div>
                              )}
                              {winners[0].kako_id === kakoId && (
                                <div className="absolute -top-1 -right-1 px-1 bg-white text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                              )}
                            </div>
                            <p className="text-yellow-300 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-bold">
                              {winners[0].kako_nickname}
                            </p>
                            <p className="text-yellow-400 text-xs font-bold">{winners[0].total_points.toLocaleString()} ⭐</p>
                            {weeklyPrizes[1] > 0 && (
                              <p className="text-green-400 text-xs font-bold">+{weeklyPrizes[1].toLocaleString()}</p>
                            )}
                            <div className="mt-1 bg-gradient-to-t from-yellow-600 to-amber-400 w-20 sm:w-24 h-24 sm:h-28 rounded-t-lg flex items-center justify-center shadow-lg shadow-yellow-500/30">
                              <span className="text-4xl sm:text-5xl animate-pulse">🥇</span>
                            </div>
                          </div>
                        )}
                        
                        {/* 3º Lugar */}
                        {winners[2] && (
                          <div className="flex flex-col items-center">
                            <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-amber-600 bg-amber-700 mb-1 ${winners[2].kako_id === kakoId ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-purple-900' : ''}`}>
                              {winners[2].kako_avatar ? (
                                <img src={winners[2].kako_avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-lg">👤</div>
                              )}
                              {winners[2].kako_id === kakoId && (
                                <div className="absolute -top-1 -right-1 px-1 bg-yellow-500 text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                              )}
                            </div>
                            <p className="text-amber-400 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-medium">
                              {winners[2].kako_nickname}
                            </p>
                            <p className="text-amber-500 text-xs">{winners[2].total_points.toLocaleString()} ⭐</p>
                            {weeklyPrizes[3] > 0 && (
                              <p className="text-green-400 text-[10px] font-bold">+{weeklyPrizes[3].toLocaleString()}</p>
                            )}
                            <div className="mt-1 bg-gradient-to-t from-amber-700 to-amber-500 w-14 sm:w-18 h-12 sm:h-16 rounded-t-lg flex items-center justify-center">
                              <span className="text-2xl sm:text-3xl">🥉</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Posição do Usuário (se não está no TOP 3) */}
                      {userData?.position && userData.position > 3 && userData.data && (
                        <div className="mt-4 p-3 bg-gradient-to-r from-purple-800/50 to-purple-700/50 rounded-xl border border-purple-500/30">
                          <p className="text-purple-300 text-xs mb-2 font-medium">📍 Sua posição no ranking:</p>
                          <RankingItem winner={userData.data} isCurrentUser={true} />
                          {userData.pointsToClimb > 0 && (
                            <p className="text-purple-300 text-xs mt-2 text-center">
                              Falta <span className="text-yellow-400 font-bold">{userData.pointsToClimb.toLocaleString()} ⭐</span> para subir para {userData.position - 1}º lugar
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Botão Ver Completo */}
                      {winners.length > 3 && (
                        <button
                          onClick={() => setShowFullRanking(true)}
                          className="w-full mt-4 py-2 px-4 bg-purple-800/50 hover:bg-purple-700/50 text-purple-200 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <span>Ver Ranking Completo (TOP {winners.length})</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </>
                  );
                }
                
                // Ranking Expandido: Manter pódio TOP 3 + Lista do 4º em diante
                const winnersFrom4th = winners.filter(w => w.rank > 3);
                
                return (
                  <>
                    {/* Pódio TOP 3 permanece visível */}
                    <div className="flex justify-around items-end gap-2 mb-4">
                      {/* 2º Lugar */}
                      {winners[1] && (
                        <div className="flex flex-col items-center">
                          <div className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-gray-400 bg-gray-600 mb-1 ${winners[1].kako_id === kakoId ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-purple-900' : ''}`}>
                            {winners[1].kako_avatar ? (
                              <img src={winners[1].kako_avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xl">👤</div>
                            )}
                            {winners[1].kako_id === kakoId && (
                              <div className="absolute -top-1 -right-1 px-1 bg-yellow-500 text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                            )}
                          </div>
                          <p className="text-gray-300 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-medium">
                            {winners[1].kako_nickname}
                          </p>
                          <p className="text-gray-400 text-xs">{winners[1].total_points.toLocaleString()} ⭐</p>
                          {weeklyPrizes[2] > 0 && (
                            <p className="text-green-400 text-[10px] font-bold">+{weeklyPrizes[2].toLocaleString()}</p>
                          )}
                          <div className="mt-1 bg-gradient-to-t from-gray-500 to-gray-400 w-16 sm:w-20 h-16 sm:h-20 rounded-t-lg flex items-center justify-center">
                            <span className="text-3xl sm:text-4xl">🥈</span>
                          </div>
                        </div>
                      )}
                      
                      {/* 1º Lugar */}
                      {winners[0] && (
                        <div className="flex flex-col items-center">
                          <div className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-3 border-yellow-400 bg-yellow-600 mb-1 shadow-lg shadow-yellow-500/50 ${winners[0].kako_id === kakoId ? 'ring-2 ring-white ring-offset-2 ring-offset-purple-900' : ''}`}>
                            {winners[0].kako_avatar ? (
                              <img src={winners[0].kako_avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-2xl">👤</div>
                            )}
                            {winners[0].kako_id === kakoId && (
                              <div className="absolute -top-1 -right-1 px-1 bg-white text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                            )}
                          </div>
                          <p className="text-yellow-300 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-bold">
                            {winners[0].kako_nickname}
                          </p>
                          <p className="text-yellow-400 text-xs font-bold">{winners[0].total_points.toLocaleString()} ⭐</p>
                          {weeklyPrizes[1] > 0 && (
                            <p className="text-green-400 text-xs font-bold">+{weeklyPrizes[1].toLocaleString()}</p>
                          )}
                          <div className="mt-1 bg-gradient-to-t from-yellow-600 to-amber-400 w-20 sm:w-24 h-24 sm:h-28 rounded-t-lg flex items-center justify-center shadow-lg shadow-yellow-500/30">
                            <span className="text-4xl sm:text-5xl animate-pulse">🥇</span>
                          </div>
                        </div>
                      )}
                      
                      {/* 3º Lugar */}
                      {winners[2] && (
                        <div className="flex flex-col items-center">
                          <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-amber-600 bg-amber-700 mb-1 ${winners[2].kako_id === kakoId ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-purple-900' : ''}`}>
                            {winners[2].kako_avatar ? (
                              <img src={winners[2].kako_avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lg">👤</div>
                            )}
                            {winners[2].kako_id === kakoId && (
                              <div className="absolute -top-1 -right-1 px-1 bg-yellow-500 text-purple-900 text-[8px] font-bold rounded">VOCÊ</div>
                            )}
                          </div>
                          <p className="text-amber-400 text-xs truncate max-w-[70px] sm:max-w-[90px] text-center font-medium">
                            {winners[2].kako_nickname}
                          </p>
                          <p className="text-amber-500 text-xs">{winners[2].total_points.toLocaleString()} ⭐</p>
                          {weeklyPrizes[3] > 0 && (
                            <p className="text-green-400 text-[10px] font-bold">+{weeklyPrizes[3].toLocaleString()}</p>
                          )}
                          <div className="mt-1 bg-gradient-to-t from-amber-700 to-amber-500 w-14 sm:w-18 h-12 sm:h-16 rounded-t-lg flex items-center justify-center">
                            <span className="text-2xl sm:text-3xl">🥉</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Lista do 4º em diante */}
                    <div className="border-t border-purple-500/30 pt-3">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-purple-300 text-sm font-medium">
                          4º ao {winners.length}º lugar {userData?.totalParticipants > winners.length && `(${userData.totalParticipants} total)`}
                        </p>
                        <button
                          onClick={() => setShowFullRanking(false)}
                          className="text-purple-400 text-xs hover:text-white transition-colors flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Recolher
                        </button>
                      </div>
                      
                      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                        {winnersFrom4th.map((winner) => (
                          <RankingItem 
                            key={winner.kako_id} 
                            winner={winner} 
                            isCurrentUser={winner.kako_id === kakoId}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Posição do Usuário (se não está no TOP 20) */}
                    {userData?.position && userData.position > 20 && userData.data && (
                      <div className="mt-4 pt-4 border-t border-purple-500/30">
                        <p className="text-purple-300 text-xs mb-2 font-medium">📍 Sua posição:</p>
                        <RankingItem winner={userData.data} isCurrentUser={true} />
                        {userData.pointsToClimb > 0 && (
                          <p className="text-purple-300 text-xs mt-2 text-center">
                            Falta <span className="text-yellow-400 font-bold">{userData.pointsToClimb.toLocaleString()} ⭐</span> para subir para {userData.position - 1}º lugar
                          </p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Área do Baú - NO TOPO quando disponível */}
        {progress.pending_boxes > 0 && (
          <Card className="bg-gradient-to-br from-purple-800/60 to-violet-900/60 backdrop-blur-lg border-yellow-500/40 overflow-hidden relative shadow-xl shadow-purple-500/20">
            {/* Efeitos de luz e partículas */}
            <LightBeams active={isShaking} />
            <MagicParticles active={isShaking || (isOpen && showPrize)} intensity={isOpen ? 'high' : 'normal'} />
            
            <CardContent className="p-4 sm:p-8 text-center relative z-10">
              {/* Badge de quantidade */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-purple-900 text-xs sm:text-sm font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg shadow-yellow-500/50">
                ✨ {progress.pending_boxes}x
              </div>
              
              {/* Baú */}
              <div className="py-6 sm:py-10 min-h-[180px] sm:min-h-[250px] flex items-center justify-center">
                <TreasureChest 
                  isShaking={isShaking}
                  isOpen={isOpen}
                  showPrize={showPrize}
                  prizeAmount={prizeAmount}
                  prizeName={prizeName}
                  prizeImageUrl={prizeImageUrl}
                />
              </div>
              
              {/* Resultado ou Botão */}
              {showPrize ? (
                <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                  {prizeAmount > 0 ? (
                    <>
                      <h3 className="text-2xl sm:text-3xl font-bold text-yellow-300 drop-shadow-lg">🎉 Parabéns!</h3>
                      <p className="text-emerald-300 text-sm sm:text-lg font-medium">+{prizeAmount} pontos no Ranking Semanal!</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl sm:text-2xl font-bold text-purple-300">Baú Vazio</h3>
                      <p className="text-purple-400 text-sm sm:text-base">Tente novamente na próxima!</p>
                    </>
                  )}
                  
                  {progress.pending_boxes > 1 && (
                    <Button 
                      data-testid="open-next-box-btn"
                      onClick={resetForNextBox}
                      className="mt-4 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-purple-900 font-bold px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base shadow-lg shadow-yellow-500/30"
                    >
                      Abrir Próximo ({progress.pending_boxes - 1})
                    </Button>
                  )}
                </div>
              ) : !opening && (
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-lg sm:text-xl font-bold text-yellow-300 drop-shadow-lg">
                    ✨ {progress.pending_boxes} baú{progress.pending_boxes > 1 ? 's' : ''} disponível{progress.pending_boxes > 1 ? 'is' : ''}!
                  </p>
                  <Button 
                    data-testid="open-box-btn"
                    onClick={openBox}
                    disabled={opening}
                    className="w-full py-4 sm:py-6 text-base sm:text-xl bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 hover:from-yellow-500 hover:via-amber-600 hover:to-orange-600 text-purple-900 font-bold shadow-xl shadow-yellow-500/40 disabled:opacity-50"
                  >
                    🔓 Abrir Baú
                  </Button>
                </div>
              )}
              
              {opening && !showPrize && (
                <p className="text-yellow-300 animate-pulse text-sm sm:text-base mt-4">Abrindo...</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Metas */}
        <Card className="bg-purple-900/40 backdrop-blur-lg border-purple-500/30 shadow-lg shadow-purple-500/10">
          <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-yellow-300 text-base sm:text-lg flex items-center gap-2">
              <span className="text-xl sm:text-2xl">🎯</span>
              Metas Diárias
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6 pb-3 sm:pb-6">
            {[
              { key: 'goal_1', name: 'Baú do Destino', goalNum: 1, image: chestGreen, gradient: 'from-emerald-400 to-green-500', bgColor: 'bg-emerald-500/10 border-emerald-500/30' },
              { key: 'goal_2', name: 'Baú Místico', goalNum: 2, image: chestRed, gradient: 'from-rose-400 to-red-500', bgColor: 'bg-rose-500/10 border-rose-500/30' },
              { key: 'goal_3', name: 'Baú do Segredo', goalNum: 3, image: chestPurple, gradient: 'from-violet-400 to-purple-500', bgColor: 'bg-violet-500/10 border-violet-500/30' }
            ].map((goal) => {
              const goalData = progress.goals?.[goal.key];
              const coinsToday = progress.coins_today || 0;
              const target = goalData?.target || 1;
              const progressPercent = Math.min(100, (coinsToday / target) * 100);
              
              // Verificar se este baú específico já foi aberto
              // Se boxes_today >= goalNum, significa que este baú já foi obtido
              // Se pending_boxes > 0 e reached, ainda tem baú para abrir
              const boxObtained = progress.boxes_today >= goal.goalNum;
              const hasPendingBox = goalData?.reached && progress.pending_boxes > 0;
              
              return (
                <div 
                  key={goal.key}
                  className={`p-3 sm:p-4 rounded-xl transition-all border ${
                    goalData?.reached 
                      ? 'bg-yellow-500/20 border-yellow-500/50 shadow-lg shadow-yellow-500/20' 
                      : goal.bgColor
                  }`}
                >
                  <div className="flex justify-between items-center mb-2 sm:mb-3">
                    <span className="font-bold text-white flex items-center gap-2 sm:gap-3 text-sm sm:text-base">
                      <img src={goal.image} alt={goal.name} className="w-14 h-14 sm:w-16 sm:h-16 object-contain drop-shadow-lg" />
                      {goal.name}
                    </span>
                    <span className="text-yellow-300 text-xs sm:text-sm font-mono font-bold">
                      {(target).toLocaleString()} 🪙
                    </span>
                  </div>
                  <div className="w-full bg-purple-950/60 rounded-full h-2.5 sm:h-3 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${
                        goalData?.reached ? 'from-yellow-400 to-amber-500' : goal.gradient
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  {goalData?.reached && (
                    <p className={`text-xs mt-2 flex items-center gap-1 font-bold ${
                      boxObtained && !hasPendingBox ? 'text-emerald-400' : 'text-yellow-400'
                    }`}>
                      {boxObtained && !hasPendingBox ? (
                        <><span>✅</span> Baú já aberto!</>
                      ) : (
                        <><span>✨</span> Meta atingida! Abra seu baú!</>
                      )}
                    </p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
        
        {/* Mensagem se não tem baú */}
        {progress.pending_boxes === 0 && progress.next_goal && (
          <Card className="bg-purple-900/40 backdrop-blur-lg border-purple-500/30 shadow-lg shadow-purple-500/10">
            <CardContent className="p-6 sm:p-8 text-center">
              <div className="text-5xl sm:text-7xl mb-3 sm:mb-4 opacity-60">📦</div>
              <p className="text-purple-200 mb-1 sm:mb-2 text-sm sm:text-lg">
                Recarregue mais{' '}
                <span className="text-yellow-400 font-bold">
                  {(progress.next_goal - progress.coins_today).toLocaleString()} 🪙
                </span>
              </p>
              <p className="text-white text-base sm:text-xl font-medium">para ganhar seu próximo baú!</p>
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* Conteúdo da Aba Histórico */}
        {activeTab === 'history' && (
          <Card className="bg-purple-900/40 backdrop-blur-lg border-purple-500/30 shadow-lg shadow-purple-500/10">
            <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6 pb-2">
              <CardTitle className="text-yellow-300 text-base sm:text-lg flex items-center gap-2">
                <span className="text-lg sm:text-xl">📜</span>
                Histórico de Prêmios
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {history.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1 sm:pr-2">
                  {history.map((item, idx) => (
                    <HistoryItem key={idx} item={item} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3 opacity-50">📜</div>
                  <p className="text-purple-300 text-sm">
                    Nenhum prêmio no histórico ainda.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Conteúdo da Aba Regras */}
        {activeTab === 'rules' && (
          <Card className="bg-purple-900/40 backdrop-blur-lg border-purple-500/30 shadow-lg shadow-purple-500/10">
            <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6 pb-2">
              <CardTitle className="text-yellow-300 text-base sm:text-lg flex items-center gap-2">
                <span className="text-lg sm:text-xl">📋</span>
                Regras do Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              {rules ? (
                <div 
                  className="prose prose-sm prose-invert max-w-none text-purple-100"
                  style={{
                    lineHeight: '1.7',
                  }}
                  dangerouslySetInnerHTML={{ __html: rules }}
                />
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3 opacity-50">📋</div>
                  <p className="text-purple-300 text-sm">
                    As regras do evento ainda não foram definidas.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Modal de Regras do Jackpot - Português/Espanhol */}
      {showRulesModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowRulesModal(false)}
        >
          <div 
            className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-purple-900 to-purple-800 border-2 border-purple-400/50 shadow-2xl shadow-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-800 to-purple-900 p-4 border-b border-purple-500/30 rounded-t-2xl">
              <button 
                onClick={() => setShowRulesModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-xl font-bold text-center text-yellow-300 flex items-center justify-center gap-2">
                🎰 {rulesLang === 'pt' ? 'Regras do Baú Jackpot' : 'Reglas del Cofre Jackpot'}
              </h2>
              
              {/* Seletor de Idioma */}
              <div className="flex justify-center gap-2 mt-3">
                <button
                  onClick={() => setRulesLang('pt')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                    rulesLang === 'pt' 
                      ? 'bg-yellow-500 text-purple-900' 
                      : 'bg-purple-700/50 text-gray-300 hover:bg-purple-600/50'
                  }`}
                >
                  🇧🇷 Português
                </button>
                <button
                  onClick={() => setRulesLang('es')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                    rulesLang === 'es' 
                      ? 'bg-yellow-500 text-purple-900' 
                      : 'bg-purple-700/50 text-gray-300 hover:bg-purple-600/50'
                  }`}
                >
                  🇪🇸 Español
                </button>
              </div>
            </div>
            
            {/* Conteúdo das Regras */}
            <div className="p-5 space-y-5 text-gray-200 text-sm">
              
              {/* O que é */}
              <div>
                <h3 className="text-yellow-400 font-bold mb-2 flex items-center gap-2">
                  <span className="text-lg">🎁</span> 
                  {rulesLang === 'pt' ? 'O que é o Baú Jackpot?' : '¿Qué es el Cofre Jackpot?'}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {rulesLang === 'pt' 
                    ? 'O Baú Jackpot é um evento especial onde você pode ganhar moedas KAKO grátis! Fique de olho na barra de progresso e esteja pronto para abrir o baú quando ele for liberado.'
                    : 'El Cofre Jackpot es un evento especial donde puedes ganar monedas KAKO gratis! Mantente atento a la barra de progreso y prepárate para abrir el cofre cuando se libere.'
                  }
                </p>
              </div>
              
              {/* Como Participar */}
              <div className="bg-purple-800/50 rounded-xl p-4 border border-purple-500/30">
                <h3 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
                  <span className="text-lg">📋</span> 
                  {rulesLang === 'pt' ? 'Como Participar' : 'Cómo Participar'}
                </h3>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 font-bold w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    <div>
                      <p className="font-medium text-white">
                        {rulesLang === 'pt' ? 'Aguarde o Progresso' : 'Espera el Progreso'}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {rulesLang === 'pt' 
                          ? 'Acompanhe a barra de progresso. Quando atingir 100%, o baú será liberado!'
                          : 'Sigue la barra de progreso. ¡Cuando llegue al 100%, el cofre será liberado!'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 font-bold w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    <div>
                      <p className="font-medium text-white">
                        {rulesLang === 'pt' ? 'Abra o Baú' : 'Abre el Cofre'}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {rulesLang === 'pt' 
                          ? 'Seja rápido! Clique no botão "Abrir Baú" para tentar coletar as moedas.'
                          : '¡Sé rápido! Haz clic en el botón "Abrir Cofre" para intentar recolectar las monedas.'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <span className="bg-yellow-500 text-purple-900 font-bold w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    <div>
                      <p className="font-medium text-white">
                        {rulesLang === 'pt' ? 'Resultado' : 'Resultado'}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {rulesLang === 'pt' 
                          ? 'Se você conseguir, as moedas serão creditadas automaticamente na sua conta KAKO!'
                          : '¡Si lo logras, las monedas se acreditarán automáticamente en tu cuenta KAKO!'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sistema de Sorte */}
              <div>
                <h3 className="text-yellow-400 font-bold mb-2 flex items-center gap-2">
                  <span className="text-lg">🍀</span> 
                  {rulesLang === 'pt' ? 'Sistema de Sorte' : 'Sistema de Suerte'}
                </h3>
                <p className="text-gray-300 mb-3 leading-relaxed">
                  {rulesLang === 'pt' 
                    ? <>Toda vez que você tenta abrir o baú e não consegue coletar moedas, você acumula <span className="text-yellow-300 font-medium">Pontos de Sorte</span>. Esses pontos aumentam suas chances de ganhar no próximo baú!</>
                    : <>Cada vez que intentas abrir el cofre y no logras recolectar monedas, acumulas <span className="text-yellow-300 font-medium">Puntos de Suerte</span>. ¡Estos puntos aumentan tus posibilidades de ganar en el próximo cofre!</>
                  }
                </p>
                
                <div className="bg-black/30 rounded-lg p-3 space-y-2">
                  <p className="text-xs text-gray-400 mb-2">
                    {rulesLang === 'pt' ? 'Níveis de Sorte:' : 'Niveles de Suerte:'}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-green-400 font-medium text-xs">
                      {rulesLang === 'pt' ? 'Sorte Baixa' : 'Suerte Baja'}
                    </span>
                    <span className="text-gray-500 text-xs">
                      - {rulesLang === 'pt' ? 'Chances normais' : 'Probabilidades normales'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    <span className="text-yellow-400 font-medium text-xs">
                      {rulesLang === 'pt' ? 'Sorte Média' : 'Suerte Media'}
                    </span>
                    <span className="text-gray-500 text-xs">
                      - {rulesLang === 'pt' ? 'Chances aumentadas' : 'Probabilidades aumentadas'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-red-400 font-medium text-xs">
                      {rulesLang === 'pt' ? 'Sorte Alta' : 'Suerte Alta'}
                    </span>
                    <span className="text-gray-500 text-xs">
                      - {rulesLang === 'pt' ? 'Chances muito aumentadas!' : '¡Probabilidades muy aumentadas!'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Dicas */}
              <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
                <h3 className="text-yellow-400 font-bold mb-2 flex items-center gap-2">
                  <span className="text-lg">💡</span> 
                  {rulesLang === 'pt' ? 'Dicas Importantes' : 'Consejos Importantes'}
                </h3>
                <ul className="space-y-2 text-gray-300 text-xs">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>
                      {rulesLang === 'pt' 
                        ? <><strong>Fique atento</strong> - O baú pode ser liberado a qualquer momento quando o progresso atingir 100%</>
                        : <><strong>Mantente atento</strong> - El cofre puede ser liberado en cualquier momento cuando el progreso llegue al 100%</>
                      }
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>
                      {rulesLang === 'pt' 
                        ? <><strong>Permaneça na página</strong> - Se você sair da página do Jackpot, seus pontos de sorte são zerados</>
                        : <><strong>Permanece en la página</strong> - Si sales de la página del Jackpot, tus puntos de suerte se reinician</>
                      }
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>
                      {rulesLang === 'pt' 
                        ? <><strong>Clique rápido</strong> - Quando o baú liberar, clique o mais rápido possível para participar</>
                        : <><strong>Haz clic rápido</strong> - Cuando el cofre se libere, haz clic lo más rápido posible para participar</>
                      }
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>
                      {rulesLang === 'pt' 
                        ? <><strong>Não desista</strong> - Mesmo que não ganhe, você acumula sorte para a próxima tentativa</>
                        : <><strong>No te rindas</strong> - Aunque no ganes, acumulas suerte para el próximo intento</>
                      }
                    </span>
                  </li>
                </ul>
              </div>
              
              {/* Termos */}
              <div className="border-t border-purple-500/30 pt-4">
                <p className="text-gray-500 text-[10px] leading-relaxed">
                  <strong className="text-gray-400">
                    {rulesLang === 'pt' ? 'Termos e Condições:' : 'Términos y Condiciones:'}
                  </strong>{' '}
                  {rulesLang === 'pt' 
                    ? 'A Fox Seller se reserva ao direito de alterar as regras ou dinâmica do evento a qualquer momento, realizar manutenções técnicas sem aviso prévio, e modificar valores de prêmios e configurações do evento. Todas as alterações visam proteger a integridade e garantir o melhor funcionamento do evento para todos os participantes.'
                    : 'Fox Seller se reserva el derecho de cambiar las reglas o la dinámica del evento en cualquier momento, realizar mantenimientos técnicos sin previo aviso y modificar los valores de los premios y la configuración del evento. Todos los cambios tienen como objetivo proteger la integridad y garantizar el mejor funcionamiento del evento para todos los participantes.'
                  }
                </p>
              </div>
              
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-gradient-to-r from-purple-800 to-purple-900 p-4 border-t border-purple-500/30 rounded-b-2xl">
              <button
                onClick={() => setShowRulesModal(false)}
                className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-purple-900 font-bold py-3 rounded-xl transition-all"
              >
                {rulesLang === 'pt' ? 'Entendi! Boa sorte! 🍀' : '¡Entendido! ¡Buena suerte! 🍀'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Resultado do Jackpot */}
      {showResultModal && jackpotResult && !jackpotResult.participating && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowResultModal(false)}
        >
          <div 
            className={`relative w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl transform transition-all ${
              jackpotResult.won 
                ? 'bg-gradient-to-br from-green-900 to-emerald-800 border-2 border-green-400 shadow-green-500/50' 
                : 'bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-gray-600 shadow-gray-500/30'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botão Fechar */}
            <button 
              onClick={() => setShowResultModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {jackpotResult.won ? (
              <>
                <div className="text-6xl mb-4 animate-bounce">🎉</div>
                <h2 className="text-2xl font-bold text-green-300 mb-2">PARABÉNS!</h2>
                <p className="text-white text-lg mb-4">
                  Você ganhou <span className="text-yellow-400 font-bold">{jackpotResult.prize_coins?.toLocaleString()}</span> moedas!
                </p>
                <p className="text-green-200 text-sm">Moedas creditadas na sua conta!</p>
                
                {/* Mostrar outros ganhadores se houver múltiplos */}
                {jackpotResult.winners && jackpotResult.winners.length > 1 && (
                  <div className="mt-4 pt-3 border-t border-green-600/30">
                    <p className="text-green-200/70 text-xs mb-2">Outros ganhadores:</p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {jackpotResult.winners
                        .filter(w => w.kako_id !== jackpotResult.winner?.kako_id)
                        .sort((a, b) => (b.prize_coins || 0) - (a.prize_coins || 0))
                        .map((w, idx) => (
                          <div key={idx} className="flex items-center justify-center gap-2 text-xs">
                            <span className="text-white/70 truncate max-w-[100px]">{w.kako_nickname}</span>
                            <span className="text-yellow-400 font-bold">{w.prize_coins?.toLocaleString()} 🪙</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-6xl mb-4">😅</div>
                <h2 className="text-xl font-bold text-gray-300 mb-3">Não foi dessa vez!</h2>
                
                {/* Mostrar múltiplos ganhadores ou ganhador único */}
                {jackpotResult.winners && jackpotResult.winners.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-gray-400 text-sm mb-2">
                      {jackpotResult.winners.length > 1 ? 'Ganhadores:' : 'Ganhou o prêmio:'}
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {jackpotResult.winners
                        .sort((a, b) => (b.prize_coins || 0) - (a.prize_coins || 0))
                        .map((winner, idx) => (
                        <div key={idx} className="flex items-center justify-center gap-3 p-2 bg-black/30 rounded-xl">
                          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-yellow-500 flex-shrink-0">
                            {winner.kako_avatar ? (
                              <img src={winner.kako_avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-yellow-600 flex items-center justify-center text-lg">👤</div>
                            )}
                          </div>
                          <div className="text-left flex-1 min-w-0">
                            <p className="text-yellow-300 font-bold text-sm truncate">
                              {winner.kako_nickname}
                            </p>
                          </div>
                          <span className="text-yellow-400 font-bold text-sm">
                            {winner.prize_coins?.toLocaleString()} 🪙
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : jackpotResult.last_winner && (
                  <div className="flex items-center justify-center gap-3 p-3 bg-black/30 rounded-xl">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-yellow-500 flex-shrink-0">
                      {jackpotResult.last_winner.kako_avatar ? (
                        <img src={jackpotResult.last_winner.kako_avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-yellow-600 flex items-center justify-center text-xl">👤</div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-yellow-300 font-bold">
                        {jackpotResult.last_winner.kako_nickname}
                      </p>
                      <p className="text-gray-400 text-sm">ganhou o prêmio!</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Countdown para próximo baú */}
            {jackpotCooldown > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-gray-400 text-xs mb-1">Próximo baú em:</p>
                <p className="text-yellow-300 font-mono text-xl font-bold">
                  {formatTimeMinSec(jackpotCooldown)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente do item de histórico com countdown para retry
const HistoryItem = ({ item }) => {
  // Sistema migrado: baús dão PONTOS (não moedas). Pontos contam pro Mega Sortudos.
  const pointsWon = item.points_won || 0;
  const prizeName = item.prize_name || '';
  
  return (
    <div 
      className={`p-2.5 sm:p-4 rounded-xl transition-all ${
        item.won 
          ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/40 shadow-lg shadow-emerald-500/10' 
          : 'bg-purple-800/30 border border-purple-500/20'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {item.prize_image_url ? (
            <img 
              src={item.prize_image_url} 
              alt={prizeName} 
              className="w-12 h-12 sm:w-14 sm:h-14 object-contain drop-shadow-lg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = item.goal_reached === 1 ? chestGreen : item.goal_reached === 2 ? chestRed : chestPurple;
              }}
            />
          ) : (
            <img 
              src={item.goal_reached === 1 ? chestGreen : item.goal_reached === 2 ? chestRed : chestPurple} 
              alt="" 
              className="w-12 h-12 sm:w-14 sm:h-14 object-contain drop-shadow-lg"
            />
          )}
          <div>
            <p className="text-white text-xs sm:text-sm font-bold">{item.date || '-'}</p>
            <p className="text-purple-300 text-xs font-medium">
              {item.goal_reached === 1 ? 'Baú do Destino' : item.goal_reached === 2 ? 'Baú Místico' : 'Baú do Segredo'}
            </p>
            {item.won && prizeName && (
              <p className="text-emerald-300 text-[10px] sm:text-xs font-semibold mt-0.5">
                {prizeName}
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          {item.won ? (
            <p className="font-bold text-sm sm:text-lg text-emerald-400">
              +{pointsWon.toLocaleString('pt-BR')} ⭐
            </p>
          ) : (
            <p className="text-purple-400 text-xs sm:text-sm font-medium">Vazio</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MysteryBoxPage;
